import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api, type GroupDirect } from "@shared/api";
import { keys } from "@shared/api";
import { Permission, byRank, has, memberGroups, rightsIn, roleColorOf, roleIconOf, type Access } from "@entities/role";
import { seedQuick, type CustomEmoji } from "@shared/lib/emoji";
import { Tile, fromPublic, fromSelf } from "@entities/user";
import { isCall, isFeed } from "@entities/channel";
import { ServerTile } from "@entities/server";
import { Dialog, Empty, Field, Hint, Notice, SettingsModal } from "@shared/ui/bits";
import { useMe, useSession } from "@entities/session";
import { rememberChannel, rememberDirect, rememberServer, useRemember } from "@shared/lib/remember";
import { useStore } from "@features/chat";
import { useRestartBlocker } from "@features/updater";
import {
  BanDialog,
  KickDialog,
  NicknameDialog,
  memberMenu,
  runVoiceAction,
  voiceMemberItems,
  type Rights,
} from "@features/moderate-member";
import { ChannelList, channelOrder, levelLabel } from "@widgets/channel-list";
import { TagChip } from "@features/server-tag";
import { RulesBar, WelcomeGate, greet } from "@features/server-rules";
import { WaitBar } from "@features/server-safety";
import { MediaViewerHost } from "@shared/ui/MediaViewer";
import { LinkPlacesProvider, type LinkPlaces } from "@shared/ui/Markup";
import {
  GROUP_MAX,
  GroupPanel,
  GroupTile,
  IncomingCall,
  PickFriends,
  RenameGroup,
  directName,
  isGroup,
  liveRings,
  useRingClock,
  useRingtone,
} from "@features/group-dm";
import { EventsEntry, NewEventDialog } from "@widgets/server-events";
import { InviteDialog, VoiceInviteRow, type InviteCandidate } from "@features/manage-invites";
import { BotModalHost, Feed, appItems, useChannelCommands } from "@widgets/chat";
import { Composer } from "@widgets/chat";
import { ForumView } from "@widgets/chat";
import { ThreadMembers, ThreadPanel } from "@widgets/chat";
import { PinnedList } from "@widgets/chat";
import { MessagePreview } from "@widgets/chat";
import { SearchField, SearchResults, ThreadList, useSearch } from "@widgets/chat";
import { ToolbarHost } from "@widgets/chat";
import { FollowDialog } from "@widgets/chat";
import { Rail, type Place } from "@widgets/server-rail";
import { QuickSwitcher, type SwitchTarget } from "@widgets/quick-switcher";
import { Inbox } from "@widgets/inbox";
import { inviteCodeOf, inviteLink, readBotInvite, readMessageLink, type BotInvite, type MessageLink } from "@shared/lib/jump";
import { dayTime, plural } from "@shared/lib/format";
import { tally } from "@shared/lib/unread";
import { CallAudio, useCall } from "@entities/call";
import { CallDock } from "@widgets/call-dock";
import { CallStrip } from "@widgets/direct-call";
import type { ServerTab } from "@widgets/server-settings";
import { Menu, MenuOn, SEPARATOR, withId, type MenuItem } from "@shared/ui/Menu";
import type { SettingsTab, Wall } from "@widgets/user-settings";
import type { ProfileServer, ProfileTab } from "@widgets/profile-card";
import { NavGrip } from "@shared/ui/NavGrip";
import { keybinds, pressedBy, useKeybinds, type KeybindAction } from "@shared/media";
import { onGlobalKey, restoreSystemKeys, setGlobalBinds } from "@shared/lib/desktop";
import { Boundary } from "@shared/ui/Boundary";
import { SelfBar } from "./SelfBar";
import { AgeGate, ageConfirmed, confirmAge } from "./AgeGate";
import { ChatHead, type Side } from "./ChatHead";
import { Shortcuts } from "./Shortcuts";
import { NotifyDialog, PrivacyDialog } from "./ServerDialogs";
import { LinkGuard } from "@features/link-guard";
import { SERVER_TEMPLATES, createFromTemplate, type ServerTemplate } from "../model/server-templates";
import { useAfk } from "../model/afk";
import { useAppTitlebar } from "@widgets/titlebar";
import type { Category, Channel, DirectChannel, Member, Named, PublicUser, Role, ServerSticker, ServerSummary, Uuid } from "@shared/api";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Spinner } from "@shared/ui/spinner";
import { useStreamer } from "@shared/ui/streamer";
import {
  IconCall,
  IconChat,
  IconChevronDown,
  IconEye,
  IconClose,
  IconMembers,
  IconMenu,
  IconMicOff,
  IconPinned,
  IconSearch,
  IconTimer,
  IconPlus,
  IconInvite,
  IconSettings,
  IconAddChannel,
  IconAddCategory,
  IconAddEvent,
  IconBell,
  IconPrivacy,
  IconEdit,
  IconEyeOff,
  IconLogOut,
} from "@shared/ui/icons";
import { overlaid } from "@shared/ui/hotkey";

/*
 * Настроек сервера и канала здесь нет: они не колонка, а окно поверх всего —
 * приложение под ним остаётся на месте.
 */
type Panel = "chat" | "friends";

/**
 * Что стоит в правой колонке.
 *
 * Одна величина на три вида, а не три признака: место одно, и держать их
 * порознь значит однажды оказаться с двумя открытыми разом — а вернее, с
 * закрытым сверху и забытым под ним.
 */

/*
 * Один и тот же пустой список на все случаи «состава нет».
 *
 * Не `[]` на месте: на состав опирается `useMemo`, собирающий имена, и новый
 * массив на каждой отрисовке пересобирал бы карту людей вхолостую — а её
 * постоянство держит зависимости ниже по экрану.
 */
/*
 * Что приезжает по требованию, а не при запуске.
 *
 * Всё это открывается по действию человека и живёт поверх рабочего экрана:
 * окна настроек, окно нового канала, карточка профиля, список друзей и
 * голосовой канал. Пока их не открыли, они не нужны — а весили в общем куске
 * прилично: одни настройки сервера с ролями и приглашениями это полторы
 * тысячи строк.
 *
 * Лента, список каналов, ряд серверов и поле ввода остаются обычными
 * импортами: они и есть рабочий экран, и делить их значило бы показать пустое
 * место в тот единственный миг, когда человек на него смотрит.
 *
 * Отдельные `default` в срезах не заводились: публичный вход у среза именной,
 * и подмена здесь короче, чем второй экспорт там.
 */
/**
 * Что видно, пока кусок едет.
 *
 * Своего оформления не заводится: это видно доли секунды и только при первом
 * открытии — дальше кусок уже в памяти вкладки.
 *
 * Обычная заглушка держит место того, на чьё место встала: колонки, панели,
 * ленты. Пустое место — это прыжок раскладки в обе стороны, и он заметнее
 * самой заглушки.
 *
 * Признак `over` — для кусков, которые откроются поверх всего: настройки,
 * настройки сервера и канала, новый канал. Держать им нечего: сами окна
 * уезжают в портал, а заглушка стоит там, где написана, — внизу оболочки, —
 * и в потоке раздвигала бы то, что окну не принадлежит. Поэтому она вне
 * потока и мыши не мешает: нажатие проходит насквозь.
 */
function Loading({ over }: { over?: boolean }) {
  if (over) {
    // Крупнее обычной: посреди пустого экрана кольцо в 14px читается точкой,
    // а не признаком, что что-то происходит.
    return (
      <div className="chunk-veil">
        <Spinner size={32} />
      </div>
    );
  }

  return (
    <div className="chunk-fill">
      <Spinner />
    </div>
  );
}

const Settings = lazy(() => import("@widgets/user-settings").then((m) => ({ default: m.Settings })));
const Portal = lazy(() => import("@widgets/developers").then((m) => ({ default: m.Portal })));
const AuthorizeBot = lazy(() => import("@widgets/developers").then((m) => ({ default: m.AuthorizeBot })));
const ServerSettings = lazy(() =>
  import("@widgets/server-settings").then((m) => ({ default: m.ServerSettings })),
);
const ChannelSettings = lazy(() =>
  import("@widgets/channel-settings").then((m) => ({ default: m.ChannelSettings })),
);
const ChannelNew = lazy(() => import("@features/create-channel").then((m) => ({ default: m.ChannelNew })));
const CategoryNew = lazy(() => import("@features/create-channel").then((m) => ({ default: m.CategoryNew })));
const Roles = lazy(() => import("@features/manage-roles").then((m) => ({ default: m.Roles })));
const Profile = lazy(() => import("@widgets/profile-card").then((m) => ({ default: m.Profile })));
const ProfilePopout = lazy(() => import("@widgets/profile-card").then((m) => ({ default: m.ProfilePopout })));
const ProfileModal = lazy(() => import("@widgets/profile-card").then((m) => ({ default: m.ProfileModal })));
const Friends = lazy(() => import("@widgets/friends").then((m) => ({ default: m.Friends })));
const Voice = lazy(() => import("@widgets/voice").then((m) => ({ default: m.Voice })));

const EMPTY_MEMBERS: Member[] = [];
const EMPTY_ROLES: Role[] = [];

const withoutDefault = (roles: Role[]) => roles.filter((role) => !role.is_default);

export function Shell() {
  const { refreshMe } = useSession();
  const me = useMe();
  const store = useStore();
  const cache = useQueryClient();
  /*
   * Звонок живёт выше экрана канала: уйти в переписку, не бросив трубку, —
   * обычное дело, и обрывать соединение на смену вкладки нельзя.
   */
  const call = useCall();
  /*
   * «Пригласить в голосовой чат» закрыта (крестиком или по сроку) — до
   * следующего входа в звонок, как в Discord. Пометка живёт здесь, а не в
   * списке каналов: тот размонтируется, стоит уйти в личку или на другой
   * сервер, и по возвращении строка появлялась снова.
   */
  const liveIn = call.state === "live" ? call.channelId : null;
  const [inviteShut, setInviteShut] = useState<Uuid | null>(null);
  const [seenCall, setSeenCall] = useState(liveIn);
  if (seenCall !== liveIn) {
    setSeenCall(liveIn);
    setInviteShut(null);
  }
  useAfk(call, me.id, (channelId) => {
    const serverId = store.serverOf(channelId);
    const server = serverId ? store.states[serverId] : undefined;
    // На сцене слушатель молчит по замыслу — переносить его за это нельзя.
    if (server?.channels.find((item) => item.id === channelId)?.channel_type === "stage") return null;
    return server?.afk_channel_id ? { channelId: server.afk_channel_id, timeout: server.afk_timeout ?? 300 } : null;
  });
  useRestartBlocker(
    call.state !== "idle"
      ? {
          id: "active-call",
          title: call.screen ? "Идёт звонок и показ экрана" : "Идёт звонок",
          detail: "для обновления нужно завершить соединение",
          kind: "confirm",
          prepare: call.leave,
        }
      : null,
  );

  /*
   * Место, на котором закрыли прошлый раз.
   *
   * Берётся сразу, до всяких загрузок: ждать список серверов значило бы
   * показать переписки и увести с них через миг. Сервера могло и не стать —
   * это проверяется ниже, когда список придёт.
   */
  const [spot, setSpot] = useState<Place>(() => {
    const server = useRemember.getState().server;
    return server ? { kind: "server", id: server } : { kind: "directs" };
  });
  const [panel, setPanel] = useState<Panel>("chat");
  const [openId, setOpenId] = useState<Uuid | null>(
    () => useRemember.getState().channel,
  );

  /*
   * Место запоминает тот, кто его меняет.
   *
   * Прежде за ним следил эффект: смотрел на состояние и после отрисовки клал
   * его в хранилище. Дверей, меняющих место, полтора десятка, и обходить их
   * все не пришлось — обе двери здесь, а половинки записи разведены в
   * `remember`: серверу известен сервер, каналу — канал.
   */
  /*
   * Место и канал — выводимые величины, а не исправляемое состояние.
   *
   * `spot` и `openId` держат **намерение**: куда человек пошёл. Что из этого
   * получилось, зависит от того, что приехало с сервера, — и раньше три
   * эффекта исправляли намерение вторым проходом: уводили с исчезнувшего
   * сервера, с сервера, откуда убрали, и открывали первый текстовый канал.
   * Каждый такой проход — лишняя отрисовка и кадр, в котором показано место,
   * которого нет.
   */

  const setPlace = useCallback((next: Place) => {
    setSpot(next);
    rememberServer(next.kind === "server" ? next.id : null);
  }, []);

  const setChannelId = useCallback((next: Uuid | null) => {
    setOpenId(next);
    rememberChannel(next);
  }, []);
  /** Полный профиль — окно поверх всего. */
  const [profileId, setProfileId] = useState<Uuid | null>(null);
  /** С какой вкладки его открыли. */
  const [profileTab, setProfileTab] = useState<ProfileTab>("activity");
  /** Почему не открылась переписка с этим человеком — окно профиля скажет это сразу. */
  const [profileNotice, setProfileNotice] = useState<{ id: Uuid; text: string } | null>(null);
  /** Всплывающая карточка: кто и по чему щёлкнули. */
  const [popout, setPopout] = useState<{ id: Uuid; anchor: HTMLElement } | null>(null);
  /*
   * Профиль — как в Discord: щелчок по человеку (есть, по чему) — карточка
   * рядом, «Профиль» в меню и аватар в карточке — полное окно.
   */
  const openFull = useCallback((id: Uuid, tab: ProfileTab = "activity") => {
    setPopout(null);
    setProfileTab(tab);
    setProfileId(id);
  }, []);
  /** Окно «Новая группа» и группа, которую переименовывают. */
  const [grouping, setGrouping] = useState(false);
  const [renamingGroup, setRenamingGroup] = useState<GroupDirect | null>(null);
  /** Открыт быстрый переход (Ctrl+K). */
  const [switching, setSwitching] = useState(false);
  /** Открыта «Почта» (Ctrl+I). */
  const [inbox, setInbox] = useState(false);
  const [shortcuts, setShortcuts] = useState(false);
  /** Куда привела ссылка на сообщение: лента того канала откроется вокруг него. */
  const [jump, setJump] = useState<{ channelId: Uuid; messageId: Uuid } | null>(null);
  /** Кого выгоняют или банят: окно с вопросом поверх всего. */
  const [moderating, setModerating] = useState<{ kind: "kick" | "ban" | "nickname"; id: Uuid; name: string; nickname?: string; self?: boolean } | null>(null);
  const [creating, setCreating] = useState(false);
  /** Новый канал: в какой категории заводим. */
  const [adding, setAdding] = useState<{ categoryId: Uuid | null; kind?: "text" | "voice" } | null>(null);
  /** Настройки какого канала открыты и не спросили ли сразу об удалении. */
  const [tuning, setTuning] = useState<{ id: Uuid; deleting: boolean } | null>(null);
  /** Чьи права категории открыты. */
  const [categoryRights, setCategoryRights] = useState<Category | null>(null);
  /*
   * «Скрыть заглушённые каналы» — у каждого сервера своё и только на этом
   * устройстве, как удобство вида. Хранилище может не открыться — тогда
   * просто показываем всё.
   */
  const [hiddenMuted, setHiddenMuted] = useState<Set<Uuid>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("airel.hide-muted") ?? "[]") as Uuid[]);
    } catch {
      return new Set();
    }
  });
  const toggleHideMuted = (id: Uuid) =>
    setHiddenMuted((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem("airel.hide-muted", JSON.stringify([...next]));
      } catch {
        /* вид сохранится до перезапуска */
      }
      return next;
    });
  /** Меню сервера: куда его поставить. */
  const [serverMenu, setServerMenu] = useState<{ x: number; y: number } | null>(null);
  /** Окно из меню сервера — какое и для какого сервера (из ряда слева — не обязательно открытого). */
  const [serverDialog, setServerDialog] = useState<{
    kind: "invite" | "category" | "event" | "notify" | "privacy";
    server: ServerSummary;
    /** Приглашение в этот канал — «Пригласить» из меню канала; нет — в открытый. */
    channel?: Channel;
  } | null>(null);
  /** Открытые настройки сервера и раздел, с которого вошли. Ничего — закрыты. */
  const [serverTab, setServerTab] = useState<ServerTab | null>(null);
  /**
   * «Просмотр сервера от лица роли», как в Discord: список каналов — только
   * те, что видит роль, и полоса «вы смотрите…» с выходом в настройки ролей.
   */
  const [asRole, setAsRole] = useState<{ serverId: Uuid; name: string; channels: ReadonlySet<Uuid> } | null>(null);
  const [theme, setTheme] = useState<"dark" | "light">(
    () => (localStorage.getItem("airel.theme") as "dark" | "light") ?? "dark",
  );
  /*
   * Тон обоев. Живёт рядом с темой и по тем же правилам: на этом устройстве,
   * а не на сервере. Обои — то, из-под чего смотрят на стекло; переносить их
   * на другую машину значит переносить туда чужой свет в комнате.
   */
  const [wall, setWall] = useState<Wall>(
    () => (localStorage.getItem("airel.wall") as Wall) ?? "aurora",
  );
  /** На узком экране за раз видно одно: навигация или лента. */
  const [mobileView, setMobileView] = useState<"nav" | "feed">("nav");
  /** С какого раздела открыть окно настроек: из меню устройств приходят на звук. */
  /*
   * Настройки человека: с какого раздела открыты. `null` — закрыты вовсе.
   *
   * Одним состоянием, а не признаком «открыты» рядом с разделом: «закрыты, но
   * на разделе звука» — положение, которого не бывает, и выразить его лучше
   * не давать.
   */
  const [settingsAt, setSettingsAt] = useState<SettingsTab | null>(null);
  /** Портал разработчика и приглашение бота по ссылке `#oauth2/authorize`. */
  const [developers, setDevelopers] = useState(false);
  const [botInvite, setBotInvite] = useState<BotInvite | null>(null);

  /*
   * Что выбрано в шапке ленты. Переживает перезапуск: колонку закрывают,
   * чтобы освободить место ленте, и находить её назавтра снова раскрытой
   * значило бы отменять решение каждым запуском. Умолчание — участники: так
   * колонка и стояла, пока её нельзя было закрыть.
   */
  const [picked, setPicked] = useState<Side | null>(() => {
    const saved = localStorage.getItem("airel.side");
    // Закреплённые и поиск — не колонки, которые помнят: первые — окном в
    // шапке, второй открывается запросом. Помнится только колонка участников.
    if (saved === "") return null;
    return "members";
  });

  /*
   * Ветку носит стор переписки — она знает, какая она именно, и открывают её
   * не только значком в шапке, но и из строки сообщения. Поэтому стор здесь
   * главный: пока в нём ветка, в колонке ветка.
   */
  /*
   * Ветка из чата голосового канала на весь экран не встаёт: середину там
   * занимает звонок. Она открывается сбоку, как обычно.
   */
  const panelServer = store.panel ? store.serverOf(store.panel.channelId) : null;
  const panelType = panelServer
    ? store.states[panelServer]?.channels.find((item) => item.id === store.panel?.channelId)?.channel_type
    : undefined;
  const wide = !!store.panel?.full && !isCall(panelType ?? "text");
  const side: Side | null = store.panel && !wide ? "threads" : picked;
  /**
   * Ветка на весь экран — на месте ленты, как в Discord; справа тогда не
   * участники сервера, а те, кто в ветке.
   */
  const fullThread = store.panel?.kind === "thread" && wide ? store.panel : null;
  /** Кто в ветке справа у ветки на весь экран — показан, пока его не скрыли значком в шапке. */
  const [threadPeople, setThreadPeople] = useState(true);

  /*
   * Ветку открыли мимо шапки. Выбор шапки тогда надо снять, иначе закрытие
   * ветки вернёт то, что было открыто до неё, — а закрыть должно значить
   * закрыть.
   *
   * Правкой прямо при отрисовке, а не эффектом: React пересчитает этот же
   * проход, и кадра с двумя открытыми не будет вовсе.
   */
  if (store.panel && !wide && picked) setPicked(null);

  const showSide = (next: Side | null) => {
    // Ветка на весь экран стоит на месте ленты, а не в колонке: смена колонки её не закрывает.
    if (store.panel && !wide) store.closePanel();

    if (next === "threads") {
      setPicked(null);
      if (channelId) store.openThreadList(channelId);
      return;
    }

    setPicked(next);
    localStorage.setItem("airel.side", next ?? "");
  };
  /** Выбранная ширина левой колонки. Ничего — та, что в макете. */
  const navWidth = useRemember((state) => state.width);

  /*
   * Тему и обои записывает тот, кто их меняет, — а не эффект, глядящий на
   * результат.
   *
   * Первое положение ставится до первой отрисовки, в `main.tsx`: эффект успел
   * бы моргнуть чужим светом, и ради темы это уже было сделано там. Здесь
   * остаётся только смена по выбору человека, а у неё есть обработчик.
   */
  const changeTheme = useCallback((next: "dark" | "light") => {
    setTheme(next);
    document.documentElement.dataset.theme = next;
    localStorage.setItem("airel.theme", next);
  }, []);

  const changeWall = useCallback((next: Wall) => {
    setWall(next);
    document.documentElement.dataset.wall = next;
    localStorage.setItem("airel.wall", next);
  }, []);

  /*
   * Сервера могло не стать: его покинули с другой машины, нас убрали или
   * забанили. Список сверяется, когда придёт: пустой означает «ещё не
   * грузился», и выгонять по нему нельзя.
   *
   * Сообщение об исключении при этом остаётся на экране: увести молча значит
   * оставить человека гадать, куда делся сервер. Закрывает его он сам.
   */
  const place: Place = useMemo(() => {
    if (spot.kind !== "server") return spot;
    if (store.removedFrom?.serverId === spot.id) return { kind: "directs" };
    if (store.servers.length === 0) return spot;
    if (store.servers.some((item) => item.id === spot.id)) return spot;
    return { kind: "directs" };
  }, [spot, store.servers, store.removedFrom]);

  // Сменили сервер или ушли в переписки — поиск начинается заново, как в Discord.
  const searchHome = place.kind === "server" ? place.id : "directs";
  useEffect(() => {
    useSearch.getState().clear();
    setPicked((current) => (current === "search" ? null : current));
  }, [searchHome]);

  const serverId = place.kind === "server" ? place.id : null;
  const state = serverId ? store.states[serverId] : undefined;
  const server = store.servers.find((item) => item.id === serverId) ?? null;

  /** Ссылка на ветку с другого сервера начинается его значком и именем, как в Discord. */
  const linkPlaces = useMemo<LinkPlaces>(
    () => ({
      here: serverId,
      serverOf: (id) => {
        const found = store.servers.find((item) => item.id === id);
        return found
          ? { name: found.name, tile: <ServerTile name={found.name} id={found.id} iconId={found.icon_id} size={16} /> }
          : null;
      },
    }),
    [serverId, store.servers],
  );

  /*
   * Какой канал открыт на самом деле.
   *
   * Открытая переписка могла закрыться, а на сервере открытым может оказаться
   * канал соседнего — или никакой. Первый текстовый тогда открывается сам:
   * пустая правая часть на сервере, где каналы есть, выглядит поломкой.
   */
  const channelId: Uuid | null = useMemo(() => {
    if (place.kind === "server") {
      if (!state) return openId;
      if (openId && state.channels.some((channel) => channel.id === openId)) return openId;
      return state.channels.find((channel) => isFeed(channel.channel_type))?.id ?? null;
    }

    if (!openId) return null;
    if (store.directs.some((item) => item.channel_id === openId)) return openId;

    /*
     * Пустой список переписок означает «ещё не грузился», и выкидывать по
     * нему открытую переписку нельзя — она моргнёт и вернётся.
     *
     * Но только если мы и были в переписках. Если нас сюда увели с сервера —
     * исключили, забанили, сервер удалили, — то открыт канал того сервера, и
     * ждать его в списке переписок бессмысленно: он там не появится никогда.
     * Без этой оговорки лента исключённого сервера оставалась на экране до
     * перезагрузки: слева уже переписки, справа ещё его разговор.
     */
    return store.directs.length === 0 && spot.kind !== "server" ? openId : null;
  }, [place.kind, state, openId, store.directs, spot.kind]);

  /*
   * Ушли в другой канал — карточка закрывается: её опоры на экране больше нет.
   * Сброс — во время отрисовки, а не эффектом: так карточка не мелькнёт на
   * новом месте ни на кадр.
   */
  const where = `${place.kind}:${serverId ?? ""}:${channelId ?? ""}:${panel}`;
  const [popoutWhere, setPopoutWhere] = useState(where);
  if (popoutWhere !== where) {
    setPopoutWhere(where);
    setPopout(null);
  }
  /*
   * Ссылка одна на всё время: строки ленты запомнены и прежнюю держат, и в
   * ней не должно быть ничего, что меняется с каналом.
   */
  const openProfile = useCallback(
    (id: Uuid, anchor?: HTMLElement) => {
      if (!anchor) return openFull(id);
      // Повторный щелчок туда же закрывает карточку, как в Discord.
      setPopout((open) => (open?.id === id && open.anchor === anchor ? null : { id, anchor }));
    },
    [openFull],
  );

  /*
   * Знаки сервера для ленты, поля ввода и панели выбора.
   *
   * Имя сервера подмешивается здесь: сам знак его не носит — он и так лежит
   * в состоянии своего сервера, — а подвалу панели и подсказке имя нужно,
   * чтобы отличить свой знак от чужого.
   *
   * В личной переписке знаков нет: они принадлежат серверу, а его тут нет.
   */
  const customEmoji = useMemo(
    () => (state?.emoji ?? []).map((item) => ({ ...item, server_name: state!.name })),
    [state],
  );

  /*
   * Чем этот человек чаще всего отвечает: из этого собран ряд быстрых
   * реакций в строке сообщения.
   *
   * Раз за сеанс и навсегда свежее (`staleTime: Infinity`): пятёрка самых
   * частых от одного нажатия не меняется, а чтобы ряд успевал за сегодняшним,
   * выбранное за сеанс прибавляется к присланным числам прямо в памяти
   * вкладки — второго запроса это не стоит.
   *
   * Отказ проглатывается: без статистики ряд заполняется набором по
   * умолчанию, и человек этого даже не заметит.
   */
  const { data: frequentEmoji } = useQuery({
    queryKey: keys.frequentEmoji(),
    queryFn: () => api.emoji.frequent().catch(() => ({ items: [] })),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (frequentEmoji) seedQuick(frequentEmoji.items);
  }, [frequentEmoji]);

  const listed = !!server;
  const listedNow = useRef(listed);
  listedNow.current = listed;
  /** Чьё состояние уже запрошено и ещё не пришло: второй такой же запрос ни к чему. */
  const loadingServer = useRef(new Set<Uuid>());

  useEffect(() => {
    if (!serverId || store.states[serverId] || loadingServer.current.has(serverId)) return;

    /*
     * Отказ здесь проглатывается намеренно: сервер мог быть восстановленным
     * из прошлого запуска и с тех пор исчезнуть. Уводит с него проверка
     * ниже — по списку серверов, а не по одному неудачному запросу, который
     * бывает и от оборвавшейся связи.
     */
    const loading = loadingServer.current;
    loading.add(serverId);
    void store
      .loadServer(serverId)
      .catch(() => {
        // Отказ, а сервер уже в списке (пришёл, пока запрос летел), — ещё раз:
        // иначе каналы не пришли бы до перезапуска. Один раз, не по кругу.
        if (listedNow.current) return store.loadServer(serverId).catch(() => {});
      })
      .finally(() => loading.delete(serverId));
    /*
     * Стора в зависимостях нет намеренно: у склейки меняется тождество на
     * каждое событие из сокета, а состояние сервера надо попросить один раз —
     * когда оказались на сервере, которого ещё нет в каталоге. И ещё раз,
     * когда он появился в списке, если первый уже вернулся ни с чем: на
     * сервер, открытый с прошлого запуска, вступили заново, а первый запрос
     * получил отказ — без этого id не менялся и каналы не приходили до
     * перезапуска. Пока первый летит, второй не нужен.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId, listed]);

  /*
   * Ушли в другой канал или в личку — ветка прежнего закрывается, как в
   * Discord. Иначе она оставалась справа в чужом канале и в переписке —
   * поверх «Собеседника».
   */
  useEffect(() => {
    if (store.panel && store.panel.channelId !== channelId) store.closePanel();
    // Только на смену канала: открыть ветку соседнего канала — значит и перейти в него.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  /*
   * Ветка по ссылке ждёт, пока откроется её канал. На другом сервере канал
   * встаёт не сразу — сначала на первый канал сервера, — и открытая раньше
   * ветка закрывалась правилом выше. Стоит после него: на смене канала оно
   * успевает отработать первым.
   */
  const [linkedThread, setLinkedThread] = useState<{ channelId: Uuid; rootId: Uuid; threadId: Uuid } | null>(null);
  useEffect(() => {
    if (!linkedThread || linkedThread.channelId !== channelId) return;
    store.openThread(linkedThread.channelId, linkedThread.rootId, linkedThread.threadId, true);
    setLinkedThread(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedThread, channelId]);

  /*
   * Назад и вперёд по пройденному — Alt+← и Alt+→ или боковые кнопки мыши,
   * как в Discord. Шаг — место, канал и страница друзей; шаг по самой истории
   * её не пополняет. В поле ввода Alt со стрелками оставлен полю.
   */
  const trail = useRef<{ steps: Step[]; at: number; walking: Step | null }>({ steps: [], at: -1, walking: null });
  const friendsOpen = panel === "friends";
  // Сервер без состояния — канал ещё не выбран: такой полушаг в историю не идёт.
  const settled = place.kind !== "server" || !!store.states[place.id];
  useEffect(() => {
    const t = trail.current;
    const here: Step = { place, channelId, friends: friendsOpen };
    // Пришли туда, куда шагали по истории, — это не новый шаг.
    const target = t.walking;
    t.walking = null;
    if (target && sameStep(target, here)) return;
    // Шагнули, а пришли не туда (канала уже нет, с сервера ушли): шаг заменяется,
    // а то, что было «вперёд», остаётся.
    if (target && t.at >= 0) {
      t.steps[t.at] = here;
      return;
    }
    // Полушаг «сервер без канала» ждёт состояния; не пришло совсем — пишется как есть.
    if (!settled && channelId === null) return;
    if (t.at >= 0 && sameStep(t.steps[t.at], here)) return;
    t.steps = [...t.steps.slice(0, t.at + 1), here].slice(-50);
    t.at = t.steps.length - 1;
  }, [place, channelId, friendsOpen, settled]);
  const walk = useRef<(by: 1 | -1) => void>(() => {});
  walk.current = (by) => {
    const t = trail.current;
    const next = t.steps[t.at + by];
    if (!next) return;
    t.at += by;
    t.walking = next;
    setPlace(next.place);
    setPanel(next.friends ? "friends" : "chat");
    setProfileId(null);
    setMobileView("feed");
    setChannelId(next.channelId);
  };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if ((event.target as HTMLElement | null)?.closest("input, textarea, [contenteditable='true']")) return;
      if (overlaid(event)) return;
      event.preventDefault();
      walk.current(event.key === "ArrowLeft" ? -1 : 1);
    };
    const onMouse = (event: MouseEvent) => {
      if (event.button !== 3 && event.button !== 4) return;
      // Боковая кнопка бывает рацией — тогда её уже взял звонок. Над открытым
      // окном (щелчок по затемнению вне него) — тоже мимо.
      if (overlaid(event) || document.querySelector("[role=dialog], [role=alertdialog]")) return;
      event.preventDefault();
      walk.current(event.button === 3 ? -1 : 1);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mouseup", onMouse);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mouseup", onMouse);
    };
  }, []);

  /*
   * Права на сервере и на канале.
   *
   * Отказ читается как «прав нет», а не как ошибка: спрашивают их у сервера,
   * который мог и закрыться, — и показать в этот миг весь набор действий
   * значило бы пообещать то, чего человек всё равно не сделает.
   */
  const { data: serverRights = null } = useQuery({
    queryKey: keys.serverRights(serverId!),
    enabled: !!serverId,
    queryFn: () => api.permissions.onServer(serverId!).catch(() => null),
  });

  const { data: access = null } = useQuery({
    queryKey: keys.channelRights(channelId!),
    enabled: !!channelId,
    queryFn: () => rightsIn(channelId!),
  });

  /*
   * Права в канале звонка — отдельным вопросом, потому что смотреть в это
   * время можно совсем другой канал, а панель звонка решает по ним, показывать
   * ли камеру и экран.
   *
   * Ключ тот же, что у прав открытого канала, и запрос тот же: пока звонок и
   * взгляд в одном месте, ответ один на двоих и второго обращения к серверу
   * не происходит.
   */
  /*
   * Где идёт звонок — канал и сервер, которому он принадлежит.
   *
   * Ищется по всему каталогу, а не по открытому серверу: разговор продолжается
   * и тогда, когда смотришь в другое место, — в этом весь смысл панели.
   */
  const callAt = useMemo(() => {
    if (!call.channelId) return null;

    for (const state of Object.values(store.states)) {
      const channel = state?.channels.find((item) => item.id === call.channelId);
      if (state && channel) return { serverId: state.id, server: state.name, channel: channel.name };
    }

    const inDirect = store.directs.find((item) => item.channel_id === call.channelId);
    if (inDirect) return { serverId: null, server: "личная переписка", channel: directName(inDirect) };

    return { serverId: null, server: "", channel: "звонок" };
  }, [call.channelId, store.states, store.directs]);

  /*
   * Состав каналов для списка — серверный `store.calls`, но себя в своём же
   * канале держим от локального звонка, а не от событий.
   *
   * События входа и выхода приходят по `user_id`, а при переподключении к
   * звонку старый сеанс присылает запоздавший `delete` уже после `create`
   * нового — и он стирает только что добавленного себя. В гриде это не видно:
   * тот живёт от самой комнаты LiveKit. Здесь источник правды тот же — пока
   * звонок `live` в канале, себя в списке этого канала показываем наверняка.
   */
  /*
   * Но для остальных нас вычеркнул сервер — запоздавшим выходом старого
   * сеанса или сверкой, заставшей комнату без нас посреди переподключения.
   * Пропали из своего же канала, оставаясь в звонке, — напоминаем о себе:
   * сервер вернёт нас в состав, сверившись с комнатой LiveKit.
   */
  const listedIn = liveIn && store.calls[liveIn]?.some((member) => member.id === me.id) ? liveIn : null;
  const wasListed = useRef<Uuid | null>(null);
  const { reclaim } = call;
  useEffect(() => {
    if (wasListed.current && wasListed.current === liveIn && !listedIn) reclaim();
    wasListed.current = listedIn;
  }, [listedIn, liveIn, reclaim]);

  const callMembers = useMemo(() => {
    const base = store.calls;
    if (call.state !== "live" || !call.channelId || !me) return base;

    const list = base[call.channelId] ?? [];
    if (list.some((member) => member.id === me.id)) return base;

    return {
      ...base,
      [call.channelId]: [
        ...list,
        { id: me.id, microphone: call.microphone, deafened: call.deafened, live: call.screen },
      ],
    };
  }, [
    store.calls,
    call.state,
    call.channelId,
    call.microphone,
    call.deafened,
    call.screen,
    me,
  ]);

  /** Часы вызовов: вызов в звонок переписки гаснет сам по сроку. */
  const ringNow = useRingClock(store.rings);

  /**
   * Войти в звонок переписки; `video` — и сразу с камерой, как «присоединиться
   * с камерой» в Discord. Камера включается, когда звонок поднялся: до того её
   * некуда публиковать.
   */
  const cameraAfterJoin = useRef<Uuid | null>(null);
  const joinDirect = (id: Uuid, video = false) => {
    cameraAfterJoin.current = video ? id : null;
    void call.join(id);
  };
  /** Звонок из карточки человека — как в Discord: личка открывается, звонок начинается. */
  const callPerson = async (id: Uuid) => {
    try {
      const opened = await api.directs.open(id);
      await store.reloadDirects();
      setPopout(null);
      openDirect(opened.channel_id);
      joinDirect(opened.channel_id);
    } catch (error) {
      setProfileNotice({
        id,
        text:
          error instanceof ApiError && error.code === "dms_closed"
            ? "Этот человек принимает личные сообщения только от друзей."
            : "Не получилось начать звонок",
      });
      openFull(id);
    }
  };
  useEffect(() => {
    if (call.state !== "live" || !cameraAfterJoin.current || call.channelId !== cameraAfterJoin.current) return;
    cameraAfterJoin.current = null;
    if (!call.camera) void call.toggleCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.state, call.channelId]);

  // «Присоединиться к звонку» из ленты: просьба приходит через стор — звонок держим здесь.
  const requested = store.callRequest;
  useEffect(() => {
    if (!requested) return;
    store.requestCall(null);
    joinDirect(requested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested]);

  /*
   * Гудки звонящему — как в Discord: пока он в звонке один, а кому-то звонят.
   * Ответили, отклонили или срок вышел — смолкают.
   */
  const ringingOut =
    call.state === "live" &&
    !!call.channelId &&
    !!me &&
    (callMembers[call.channelId] ?? []).every((member) => member.id === me.id) &&
    liveRings(store.rings[call.channelId], ringNow).some((ring) => ring.user_id !== me.id);
  useRingtone("outgoing", ringingOut);

  /** «Участвует в звонке» — у пары, «В звонке: N» — у группы; `null` — звонка без нас нет. */
  const callStatusOf = (item: DirectChannel): string | null => {
    const others = (callMembers[item.channel_id] ?? []).filter((member) => member.id !== me?.id);
    if (others.length === 0) return null;
    return isGroup(item) ? `В звонке: ${others.length}` : "Участвует в звонке";
  };

  const { data: callRights = null } = useQuery({
    queryKey: keys.channelRights(call.channelId!),
    enabled: !!call.channelId,
    queryFn: () => rightsIn(call.channelId!),
  });

  /*
   * Участники сервера грузятся здесь, а не в колонке справа: их имена нужны
   * и ей, и звонку. Без этого сосед по серверу в голосовом канале
   * назывался бы «участником» — имена брались только из друзей и переписок.
   *
   * Перезапрашивается по отметке из общего состояния: вступление приходит
   * событием, и одной загрузки при заходе мало — вступивший при нас иначе
   * так и остался бы безымянным до перезагрузки страницы. Помечает его
   * устаревшим сам обработчик события — в `store.tsx`, — и ключ здесь тот же,
   * что у окна настроек: состав один, и спрашивать его дважды незачем.
   */
  const { data: members = EMPTY_MEMBERS } = useQuery({
    queryKey: keys.members(serverId!),
    enabled: !!serverId,
    queryFn: () =>
      api.servers
        .members(serverId!)
        .then((value) => value.items)
        .catch(() => EMPTY_MEMBERS),
  });

  /*
   * Роли сервера: их называют через «@» рядом с людьми, и по номеру же
   * упоминание показывается именем.
   *
   * Ключ тот же, что у окна настроек: список один, и спрашивать его дважды
   * незачем — правка ролей помечает устаревшим и этот запрос.
   *
   * Роль по умолчанию отсеивается здесь, а не у каждого, кто спрашивает: она
   * есть у всех, и позвать её значит позвать весь сервер. Такое просят
   * отдельным правом, которого пока нет, и сервер такое упоминание всё равно
   * не развернёт.
   */
  const { data: roles = EMPTY_ROLES } = useQuery({
    queryKey: keys.roles(serverId!),
    enabled: !!serverId,
    queryFn: () =>
      api.roles
        .list(serverId!)
        .then((value) => byRank(value.items))
        .catch(() => EMPTY_ROLES),
    /*
     * Роль по умолчанию отсекается при чтении, а не в запросе: ключ общий с
     * редактором прав, и урезанный список в кэше прятал там «Права по умолчанию».
     */
    select: withoutDefault,
  });

  /**
   * Сервер, где смотрят профиль, — для карточки и окна: роли человека на нём,
   * ник, когда пришёл, и мои права выдавать роли. В переписке его нет.
   */
  const profileServer = (id: Uuid): ProfileServer | null =>
    place.kind === "server" && server
      ? {
          id: server.id,
          name: server.name,
          iconId: server.icon_id,
          member: members.find((item) => item.id === id) ?? null,
          roles,
          rights: serverRights,
          onRole: (roleId, on) =>
            void (on ? api.roles.assign(server.id, id, roleId) : api.roles.unassign(server.id, id, roleId))
              .then(() => cache.invalidateQueries({ queryKey: keys.members(server.id) }))
              .catch(() => {}),
        }
      : null;

  /** Свои роли на этом сервере: по ним видно, что через роль позвали нас. */
  const myRoles = useMemo(
    () => members.find((member) => member.id === me?.id)?.roles ?? [],
    [members, me?.id],
  );

  /** Все, кого удалось узнать по имени: отношения, переписки, состав сервера. */
  const peopleById = useMemo(() => {
    const map = new Map<Uuid, Named>();
    for (const member of members) map.set(member.id, member);
    // Отношения и переписки идут поверх: там профиль полнее.
    for (const user of store.relationships) map.set(user.id, user);
    for (const direct of store.directs) {
      for (const user of isGroup(direct) ? direct.recipients : [direct.recipient]) map.set(user.id, user);
    }
    // Ник на сервере — поверх всего, как в Discord: здесь человека зовут так.
    // Отсюда его берут и ветки, и подсказка «@», и закреплённые.
    for (const member of members) {
      if (member.nickname) map.set(member.id, { ...(map.get(member.id) ?? member), display_name: member.nickname });
    }
    return map;
  }, [members, store.relationships, store.directs]);

  /*
   * Нас убрали с сервера — уводим оттуда, не дожидаясь, пока человек упрётся
   * в первый отказ.
   *
   * Само сообщение при этом остаётся на экране: увести молча значит оставить
   * его гадать, куда делся сервер. Закрывает его он сам.
   */
  const removed = store.removedFrom;

  /*
   * Модератор перенёс нас в другой голосовой канал. Переходим обычным входом —
   * как если бы человек нажал на канал сам; смотреть он остаётся туда же, куда
   * смотрел. Велено про звонок, из которого мы уже ушли, — ничего не делаем.
   */
  /*
   * Голосовой канал, у которого сейчас открыт чат, а не звонок. Чат у такого
   * канала тот же, что у текстового, — лента и поле ввода, — просто живёт за
   * кнопкой: смотреть приходят прежде всего звонок.
   */
  const [voiceChatOf, setVoiceChatOf] = useState<Uuid | null>(null);
  /** Каналы с возрастным ограничением, где возраст уже подтвердили. */
  const [ageOk, setAgeOk] = useState(ageConfirmed);
  /** Отказ сервера на действие модератора из списка каналов — строкой под списком. */
  const [voiceRefused, setVoiceRefused] = useState<string | null>(null);
  const voiceChat = !!channelId && voiceChatOf === channelId;

  const moved = store.movedTo;
  useEffect(() => {
    if (!moved) return;
    store.setMovedTo(null);
    if (call.channelId !== moved.from) return;
    void call.join(moved.to, true);
    // Смотрел на звонок — смотрит и дальше на него, а не на опустевший канал.
    if (channelId === moved.from) setChannelId(moved.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moved]);

  /** Вступили по карточке приглашения или открыли свой сервер из неё — как после «Вступить по коду». */
  /** `join` — приглашение в голосовой: канал открывается, и звонок начинается, как в Discord. */
  const openServer = async (id: Uuid, channel: Uuid | null, join = false) => {
    await store.reloadServers();
    setPlace({ kind: "server", id });
    setPanel("chat");
    if (channel) setChannelId(channel);
    if (channel && join && call.channelId !== channel) void call.join(channel);
  };

  const openDirect = useCallback(
    (id: Uuid) => {
      setPlace({ kind: "directs" });
      setPanel("chat");
      setChannelId(id);
      rememberDirect(id);
      setMobileView("feed");
    },
    [setPlace, setChannelId],
  );

  /*
   * Написать человеку из любого места: переписка может ещё не существовать,
   * и тогда её надо завести. Открывать её сразу — то же, что делает карточка
   * профиля: одно действие, один результат.
   */
  const writeDirect = useCallback(
    async (id: Uuid) => {
      try {
        const opened = await api.directs.open(id);
        setProfileNotice(null);
        await store.reloadDirects();
        openDirect(opened.channel_id);
      } catch (error) {
        // Переписку не открыть (только для друзей, блокировка) — окно профиля скажет почему.
        setProfileNotice({
          id,
          text:
            error instanceof ApiError && error.code === "dms_closed"
              ? "Этот человек принимает личные сообщения только от друзей."
              : error instanceof ApiError
                ? error.message
                : "Не получилось открыть переписку",
        });
        openFull(id);
      }
    },
    [openDirect, openFull, store],
  );

  /** «Сообщение для @имя» в карточке: сообщение уходит в личку, личка открывается. */
  const sendDirect = useCallback(
    async (user: PublicUser, text: string) => {
      const opened = await api.directs.open(user.id);
      await api.messages.post(opened.channel_id, text);
      await store.reloadDirects();
      setPopout(null);
      openDirect(opened.channel_id);
    },
    [openDirect, store],
  );

  /**
   * «Пригласить на сервер» — как в Discord: приглашение на неделю уходит
   * человеку в личку, и переписка открывается, чтобы было видно, что ушло.
   */
  const inviteTo = useCallback(
    async (id: Uuid, serverId: Uuid) => {
      try {
        // Сначала переписка: закрытая личка не должна оставлять за собой
        // живое приглашение, а не ушедшее — убирается.
        const opened = await api.directs.open(id);
        const invite = await api.invites.create(serverId, { max_uses: null, expires_in_hours: 168 });
        try {
          await api.messages.post(opened.channel_id, inviteLink(invite.code));
        } catch (error) {
          void api.invites.remove(invite.code).catch(() => {});
          throw error;
        }
        await store.reloadDirects();
        openDirect(opened.channel_id);
      } catch (error) {
        setProfileNotice({
          id,
          text:
            error instanceof ApiError && error.code === "dms_closed"
              ? "Этот человек принимает личные сообщения только от друзей."
              : error instanceof ApiError && error.status === 403
                ? "Не вышло: на том сервере у вас нет права приглашать, или личка закрыта."
                : "Приглашение не отправилось",
        });
        openFull(id);
      }
    },
    [openDirect, openFull, store],
  );

  /*
   * Канал считается прочитанным, только когда он открыт **и окно в фокусе**.
   *
   * Без второго условия свёрнутое окно «прочитывало» бы всё само: человек
   * ушёл обедать с открытым каналом и вернулся к чистому дереву, так и не
   * увидев, что было.
   */
  /** Настройки открыты поверх: канал под ними закрыт затемнением. */
  const covered = serverTab !== null || tuning !== null;
  /*
   * Голосовой канал без открытого чата — это звонок: ленты на экране нет, и
   * читающим канал считать нельзя. Иначе приходящее в чат канала гасилось бы
   * как прочитанное, и никто бы его не увидел.
   */
  // Ушли из канала — «отмечено непрочитанным» отпускается. Именно уход, а не
  // потеря фокуса или звонок поверх: те возвращают в тот же канал.
  useEffect(
    () => () => {
      if (channelId) store.releaseKept(channelId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channelId],
  );

  const direct = store.directs.find((item) => item.channel_id === channelId) ?? null;

  /*
   * Звонок в переписке — как в Discord: идёт он или мы в нём — середина отдана
   * звонку, лента переписки — колонкой справа (её можно спрятать).
   */
  const directCall =
    !!direct && (call.channelId === direct.channel_id || (callMembers[direct.channel_id]?.length ?? 0) > 0);
  /*
   * Звонок переписки — как в Discord: полосой над лентой. Развёрнутый — сценой
   * посреди окна с лентой справа: там сетка, трансляции и полный экран.
   */
  const [dmExpanded, setDmExpanded] = useState(false);
  const dmStage = directCall && dmExpanded;
  // Звонок кончился — следующий снова начнётся полосой.
  useEffect(() => {
    if (!directCall) setDmExpanded(false);
  }, [directCall]);
  const [dmChatHidden, setDmChatHidden] = useState(false);
  const directChannel = useMemo(
    () =>
      direct
        ? ({
            id: direct.channel_id,
            name: directName(direct),
            channel_type: "direct",
            category_id: null,
            topic: null,
            position: 0,
            private: false,
            created: direct.created,
            updated: direct.created,
            server_id: null,
          } as unknown as Channel)
        : null,
    [direct],
  );

  /* Читаем ли ленту: у звонка ленты нет, у чата сбоку — есть. */
  const callView =
    (isCall(state?.channels.find((item) => item.id === channelId)?.channel_type ?? "text") && !voiceChat) ||
    (dmStage && dmChatHidden);

  useEffect(() => {
    if (!channelId || panel !== "chat" || covered || callView) {
      store.setReading(null);
      return;
    }

    const sync = () => store.setReading(document.hasFocus() ? channelId : null);

    sync();
    window.addEventListener("focus", sync);
    window.addEventListener("blur", sync);

    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("blur", sync);
      store.setReading(null);
    };
    /*
     * В зависимостях только то, от чего зависит сам ответ «читаем ли», и
     * действие, которым он объявляется. Склейка целиком сюда не годится: она
     * меняет тождество на каждое событие, и слушатели окна пересобирались бы
     * на каждое чужое сообщение.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, panel, covered, callView, store.setReading]);

  const activeChannel: Channel | null = useMemo(() => {
    if (!state || !channelId) return null;
    return state.channels.find((channel) => channel.id === channelId) ?? null;
  }, [state, channelId]);
  const gated = !!activeChannel?.nsfw && !!channelId && !ageOk.has(channelId);

  /** В каком голосовом канале этого сервера человек сейчас — имя; нигде — `null`. */
  const voiceChannelOf = (id: Uuid): string | null => {
    const where = state?.channels.find(
      (channel) => isCall(channel.channel_type) && callMembers[channel.id]?.some((member) => member.id === id),
    );
    return where?.name ?? null;
  };

  /*
   * Кого предложить в голосовой — как в Discord: участники сервера, которых
   * нет в этом звонке. У Discord порядок — по тому, с кем человек чаще бывает в
   * голосе; такого счёта у нас нет, поэтому выше друзья, потом те, кто в сети.
   */
  const inviteCandidates = (channelId: Uuid): InviteCandidate[] => {
    const inside = new Set((callMembers[channelId] ?? []).map((member) => member.id));
    const friends = new Set(
      store.relationships.filter((user) => user.relationship === "friend").map((user) => user.id),
    );
    const weight = (member: Member) => (friends.has(member.id) ? 2 : 0) + (member.online ? 1 : 0);
    return members
      .filter((member) => member.id !== me.id && !inside.has(member.id))
      .sort((a, b) => weight(b) - weight(a))
      .map((member) => ({
        id: member.id,
        person: {
          id: member.id,
          name: member.nickname || member.display_name || member.username,
          handle: member.handle,
          avatarId: member.avatar_id,
          decorationId: member.decoration_id ?? null,
          presence: member.status_presence,
          online: member.online,
        },
        voice: voiceChannelOf(member.id),
      }));
  };

  /*
   * Кому предлагать в окне приглашения в голосовой — как в Discord: друзья и
   * те, с кем есть переписка; заблокированных нет.
   */
  const inviteContacts: PublicUser[] = (() => {
    const seen = new Map<Uuid, PublicUser>();
    for (const user of store.relationships) if (user.relationship === "friend") seen.set(user.id, user);
    for (const item of store.directs) {
      if (item.channel_type !== "group" && item.recipient.relationship !== "blocked") {
        if (!seen.has(item.recipient.id)) seen.set(item.recipient.id, item.recipient);
      }
    }
    return [...seen.values()];
  })();



  /**
   * Меню сервера — одно на два пути: кнопку с названием и правую кнопку по ней.
   *
   * Пункты считаются здесь, а не в разметке, ровно поэтому: набор один, и
   * держать его двумя копиями значило бы однажды добавить пункт в одну.
   */
  const serverItems: MenuItem[] = (() => {
    if (!state || !serverRights || !server) return [];
    const can = (bit: bigint) => serverRights.is_owner || has(serverRights.permissions, bit);
    const open = (kind: NonNullable<typeof serverDialog>["kind"]) => () => setServerDialog({ kind, server });
    const mine = members.find((member) => member.id === me.id);

    // Порядок и значки — как в меню сервера Discord, без «Буста» и «Каталога приложений».
    // Разделы настроек (роли, участники) не дублируются: они в «Настройках сервера».
    return [
      ...(can(Permission.InviteOthers)
        ? [{ label: "Пригласить на сервер", icon: IconInvite, onPick: open("invite") }]
        : []),
      { label: "Настройки сервера", icon: IconSettings, onPick: () => setServerTab("look") },
      ...(can(Permission.ManageChannel)
        ? [
            { label: "Создать канал", icon: IconAddChannel, onPick: () => setAdding({ categoryId: null }) },
            { label: "Создать категорию", icon: IconAddCategory, onPick: open("category") },
          ]
        : []),
      ...(can(Permission.CreateEvents) || can(Permission.ManageEvents)
        ? [{ label: "Создать событие", icon: IconAddEvent, onPick: open("event") }]
        : []),
      SEPARATOR,
      { label: "Параметры уведомлений", icon: IconBell, onPick: open("notify") },
      { label: "Настройки конфиденциальности", icon: IconPrivacy, onPick: open("privacy") },
      SEPARATOR,
      // Личного профиля на сервере у нас — ник: аватара и описания по серверу пока нет.
      ...(can(Permission.ChangeNickname) || can(Permission.ManageNicknames)
        ? [
            {
              label: "Редактировать личный профиль сервера",
              icon: IconEdit,
              onPick: () =>
                setModerating({
                  kind: "nickname",
                  id: me.id,
                  name: me.display_name ?? me.username,
                  nickname: mine?.nickname ?? undefined,
                  self: true,
                }),
            },
          ]
        : []),
      {
        label: "Скрыть заглушённые каналы",
        icon: IconEyeOff,
        toggle: true,
        on: hiddenMuted.has(state.id),
        onPick: () => toggleHideMuted(state.id),
      },
      // Владельцу выход не предлагается: сервер остался бы без хозяина.
      ...(serverRights.is_owner
        ? []
        : [
            SEPARATOR,
            {
              label: "Покинуть сервер",
              icon: IconLogOut,
              kind: "danger" as const,
              onPick: () => {
                void api.servers.leave(state.id).then(() => {
                  setPlace({ kind: "directs" });
                  setChannelId(null);
                  return store.reloadServers();
                });
              },
            },
          ]),
    ];
  })();

  /**
   * Перейти к сообщению по ссылке — как в Discord: на его сервер или в
   * переписку, в его канал, и лента встаёт вокруг него. В том же канале —
   * просто переставить окно.
   */
  function openMessage(link: MessageLink) {
    /*
     * Ссылка на ветку — канал и ветка в нём на весь экран, как в Discord.
     * Корень знает только сама ветка, поэтому её сначала спрашивают, а
     * открывается она, когда откроется её канал (`linkedThread`).
     */
    if (link.threadId) {
      const threadId = link.threadId;
      if (link.channelId !== channelId) {
        setJump(null);
        setPlace(link.serverId ? { kind: "server", id: link.serverId } : { kind: "directs" });
        setPanel("chat");
        setProfileId(null);
        setChannelId(link.channelId);
      }
      void api.threads
        .get(link.channelId, threadId)
        .then((thread) => setLinkedThread({ channelId: link.channelId, rootId: thread.root?.id ?? threadId, threadId }))
        .catch(() => {});
      return;
    }

    if (link.channelId === channelId) {
      if (link.messageId) void store.openAt(link.channelId, link.messageId);
      return;
    }

    // Ссылка на канал без сообщения — просто открыть канал, с его чертой.
    setJump(link.messageId ? { channelId: link.channelId, messageId: link.messageId } : null);
    setPlace(link.serverId ? { kind: "server", id: link.serverId } : { kind: "directs" });
    setPanel("chat");
    setProfileId(null);
    setChannelId(link.channelId);
  }

  /*
   * Ссылка, по которой открыли саму страницу (`…/#channels/…`), и ссылка,
   * вставленная в адресную строку уже открытой. Адрес после перехода чистится:
   * иначе обновление страницы снова уводило бы туда же.
   */
  /*
   * Слушатель ставится один раз, а переход — свежий: замкни он `openMessage`
   * первой отрисовки, он помнил бы канал на момент загрузки и ссылку на него
   * считал бы «уже здесь», даже когда человек давно на другом сервере.
   */
  const openLink = useRef(openMessage);
  openLink.current = openMessage;
  useEffect(() => {
    const follow = () => {
      const invite = readBotInvite(location.href);
      if (invite) {
        setBotInvite(invite);
        history.replaceState(null, "", location.pathname + location.search);
        return;
      }
      const link = readMessageLink(location.href);
      if (!link) return;
      openLink.current(link);
      history.replaceState(null, "", location.pathname + location.search);
    };
    follow();
    window.addEventListener("hashchange", follow);
    return () => window.removeEventListener("hashchange", follow);
  }, []);

  /*
   * Alt+↑/↓ — соседний текстовый канал (в переписках — соседняя переписка),
   * Ctrl+Alt+↑/↓ — соседний сервер, как в Discord. Обработчик читает свежее
   * состояние через ссылку: слушатель на окне ставится один раз.
   */
  const step = useRef<(event: KeyboardEvent) => void>(() => {});
  step.current = (event) => {
    const by = event.key === "ArrowDown" ? 1 : -1;
    // Соседний по кругу; если стоим не на одном из них — первый или последний.
    const next = (list: Uuid[], at: number) => {
      if (!list.length) return null;
      if (at < 0) return by > 0 ? list[0] : list[list.length - 1];
      return list[(at + by + list.length) % list.length];
    };

    const mod = event.ctrlKey || event.metaKey;
    if (mod && !event.shiftKey) {
      const ids = store.servers.map((item) => item.id);
      const target = next(ids, place.kind === "server" ? ids.indexOf(place.id) : -1);
      if (target) go({ kind: "server", id: target });
      return;
    }

    const all =
      place.kind === "server"
        ? channelOrder(state?.channels ?? [], state?.categories ?? [])
            .filter((channel) => isFeed(channel.channel_type))
            .map((channel) => channel.id)
        : store.directs.map((item) => item.channel_id);
    // Alt+Shift — только непрочитанные, Ctrl+Alt+Shift — только с упоминаниями,
    // как в Discord; текущий остаётся точкой отсчёта.
    const ids = mod
      ? all.filter((id) => id === channelId || (store.mentions[id] ?? 0) > 0)
      : event.shiftKey
        ? all.filter((id) => id === channelId || (store.unread[id] ?? 0) > 0)
        : all;
    const target = next(ids, channelId ? ids.indexOf(channelId) : -1);
    if (!target) return;
    setPanel("chat");
    setProfileId(null);
    setChannelId(target);
  };

  /*
   * Микрофон и звук — одним щелчком, откуда бы ни пришло нажатие. Свои
   * сочетания из «Системы» в окне слышит обработчик ниже, а поверх игр —
   * оболочка; одно нажатие приходит обоими путями, и второй щелчок в пределах
   * четверти секунды не считается.
   */
  const flipped = useRef<Record<KeybindAction, number>>({ mute: 0, deafen: 0 });
  const flip = useRef<(action: KeybindAction) => void>(() => {});
  flip.current = (action) => {
    const now = performance.now();
    if (now - flipped.current[action] < 250) return;
    flipped.current[action] = now;
    if (action === "mute") void call.toggleMicrophone();
    else call.toggleDeafen();
  };

  const custom = useKeybinds();
  useEffect(() => {
    setGlobalBinds("custom", custom.map(({ action, keys }) => ({ action, keys })));
    return () => setGlobalBinds("custom", []);
  }, [custom]);
  // Рацию слушает сам звонок (`usePushToTalk`), здесь — только щелчки.
  useEffect(() => {
    restoreSystemKeys();
    return onGlobalKey((action, down) => {
      if (down && action !== "ptt") flip.current(action);
    });
  }, []);

  /*
   * Микрофон и звук с клавиатуры — Ctrl+Shift+M и Ctrl+Shift+D, создать или
   * вступить — Ctrl+Shift+N, к идущему звонку — Ctrl+Alt+A, как в Discord.
   */
  const toggles = useRef<(event: KeyboardEvent) => boolean>(() => false);
  toggles.current = (event) => {
    // Своё сочетание клавишу не забирает: «M» в поле ввода остаётся буквой.
    const own = keybinds().find((bind) => pressedBy(bind.keys, event.code, event));
    if (own && !event.repeat) flip.current(own.action);
    if (!(event.ctrlKey || event.metaKey)) return false;
    // AltGr в Windows приходит как Ctrl+Alt: AltGr+A — буква (á, ą), а не переход.
    if (event.altKey && !event.shiftKey && event.code === "KeyA" && !event.getModifierState("AltGraph")) {
      const at = call.channelId;
      if (!at || overlaid(event)) return false;
      openMessage({ serverId: store.serverOf(at) ?? null, channelId: at, messageId: null });
      return true;
    }
    if (!event.shiftKey || event.altKey) return false;
    // Зажатая клавиша не щёлкает переключателем раз за разом.
    if (event.repeat) return event.code === "KeyM" || event.code === "KeyD";
    if (event.code === "KeyM") flip.current("mute");
    else if (event.code === "KeyD") flip.current("deafen");
    else if (event.code === "KeyN" && !overlaid(event)) setCreating(true);
    else return false;
    return true;
  };

  // Ctrl+K (⌘K) — быстрый переход, как в Discord. Повторное нажатие закрывает.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
        event.preventDefault();
        step.current(event);
        return;
      }
      if ((event.ctrlKey || event.metaKey) && !event.altKey && event.code === "KeyK") {
        event.preventDefault();
        setSwitching((open) => !open);
      }
      if (toggles.current(event)) {
        event.preventDefault();
        return;
      }
      // Ctrl+/ — горячие клавиши, как в Discord.
      // Клавиша «/» на месте: в русской раскладке она даёт «.», в немецкой там «-» — его не трогаем.
      if (
        (event.ctrlKey || event.metaKey) &&
        !event.altKey &&
        event.code === "Slash" &&
        (event.key === "/" || event.key === "." || event.key === "?")
      ) {
        event.preventDefault();
        setShortcuts((open) => !open);
      }
      // Ctrl+I — входящие, как в Discord.
      if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && event.code === "KeyI") {
        event.preventDefault();
        setInbox((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function switchTo(target: SwitchTarget) {
    setSwitching(false);
    if (target.kind === "server") {
      go({ kind: "server", id: target.id });
      return;
    }
    if (target.kind === "person") {
      void writeDirect(target.id);
      return;
    }

    setPlace(target.kind === "channel" ? { kind: "server", id: target.serverId } : { kind: "directs" });
    setPanel("chat");
    setProfileId(null);
    setMobileView("feed");
    setChannelId(target.id);
  }

  /** Перейти по ряду серверов: на сервер, в личные или к друзьям. */
  function go(next: Place) {
    setPlace(next);
    setProfileId(null);
    setMobileView("nav");
    /*
     * В личные — как домой в Discord: к последней переписке, а если её
     * нет — к друзьям. Пустой экран «переписок нет» при живых
     * переписках выглядел так, будто их нет вовсе.
     */
    const last = next.kind === "directs" ? useRemember.getState().direct : null;
    const alive =
      last != null && (store.directs.length === 0 || store.directs.some((item) => item.channel_id === last));
    if (next.kind === "directs" && !alive) {
      setPanel("friends");
      setChannelId(null);
      return;
    }
    setPanel(next.kind === "friends" ? "friends" : "chat");
    setChannelId(alive ? last : null);
  }

  /**
   * Меню сервера в ряду слева — как в Discord: по правой кнопке на значке,
   * для любого сервера, а не только открытого. Разделы настроек открываются
   * переходом на сервер: окно настроек показывает открытый.
   */
  const railMenu = (target: ServerSummary): MenuItem[] => {
    const level =
      store.notifyServer[target.id] ?? store.states[target.id]?.default_notify ?? target.default_notify ?? "all";
    const open = (tab: ServerTab) => {
      go({ kind: "server", id: target.id });
      setServerTab(tab);
    };

    return [
      { label: "Отметить как прочитанное", onPick: () => store.markServerRead(target.id) },
      SEPARATOR,
      { label: "Пригласить на сервер", onPick: () => setServerDialog({ kind: "invite", server: target }) },
      { label: "Настройки сервера", onPick: () => open("look") },
      {
        label: "Уведомления",
        hint: levelLabel(level),
        items: (["all", "mentions", "none"] as const).map((value) => ({
          label: levelLabel(value),
          choice: true,
          on: level === value,
          onPick: () => void store.setServerNotify(target.id, value),
        })),
      },
      // Владельцу выход не предлагается: сервер остался бы без хозяина.
      ...(target.owner_id === me?.id
        ? []
        : [
            SEPARATOR,
            {
              label: "Покинуть сервер",
              kind: "danger" as const,
              onPick: () => {
                void api.servers.leave(target.id).then(() => {
                  if (place.kind === "server" && place.id === target.id) {
                    setPlace({ kind: "directs" });
                    setChannelId(null);
                  }
                  return store.reloadServers();
                });
              },
            },
          ]),
    ];
  };

  /** Мой тайм-аут на открытом сервере: поле ввода говорит, почему писать нельзя. */
  const mine = place.kind === "server" ? members.find((member) => member.id === me?.id) : undefined;
  const myTimeout =
    mine?.timeout_until && new Date(mine.timeout_until).getTime() > Date.now() ? mine.timeout_until : null;

  /** Цвет имени участника — старшей цветной роли; в переписках ролей нет. */
  const colorOf = useMemo(() => {
    const byId = new Map(
      (place.kind === "server" ? members : []).map((member) => [member.id, roleColorOf(member.roles, roles)]),
    );
    return (userId: Uuid) => byId.get(userId) ?? null;
  }, [place.kind, members, roles]);

  const iconOf = useMemo(() => {
    const byId = new Map(
      (place.kind === "server" ? members : []).map((member) => [member.id, roleIconOf(member.roles, roles)]),
    );
    return (userId: Uuid) => byId.get(userId) ?? null;
  }, [place.kind, members, roles]);

  /** Ник на открытом сервере — в ленте он стоит вместо общего имени. */
  const nickOf = useMemo(() => {
    const byId = new Map(
      (place.kind === "server" ? members : []).flatMap((member) =>
        member.nickname ? [[member.id, member.nickname] as const] : [],
      ),
    );
    return (userId: Uuid) => byId.get(userId) ?? null;
  }, [place.kind, members]);

  // Упоминания — числом в заголовке окна, как «(3) Discord»: видно и в панели задач.
  const mentionTotal = Object.values(store.mentions).reduce((sum, count) => sum + count, 0);
  useEffect(() => {
    document.title = mentionTotal > 0 ? `(${mentionTotal > 99 ? "99+" : mentionTotal}) Airel` : "Airel";
  }, [mentionTotal]);

  const streamer = useStreamer((state) => state.on);
  // «Почта» — в полосе окна, как в Discord; счётчик — упоминания по всем серверам.
  const mentionsTotal = Object.values(store.mentions).reduce((sum, count) => sum + count, 0);
  const toggleInbox = useCallback(() => setInbox((open) => !open), []);
  useAppTitlebar({
    inbox: { count: mentionsTotal, open: toggleInbox },
    place:
      place.kind === "server"
        ? server
          ? { kind: "server", id: server.id, name: server.name, iconId: server.icon_id }
          : null
        : { kind: "directs" },
    subtitle:
      (place.kind === "server"
        ? `${server?.name ?? "…"}${activeChannel ? ` · #${activeChannel.name}` : ""}`
        : "личные переписки") + (streamer ? " · режим стримера" : ""),
    connection: store.connection,
    browserVisible: true,
  });

  /** «/kick» и «/ban» из поля ввода — то же окно, что из меню человека. Полей два: лента и ветка. */
  const moderateFromComposer = (kind: "kick" | "ban", id: Uuid) => {
    const person = peopleById.get(id);
    setModerating({ kind, id, name: person?.display_name || person?.username || "участника" });
  };

  /** «/msg» — написать в личку, не уходя из канала. */
  const directFromComposer = async (id: Uuid, content: string) => {
    const opened = await api.directs.open(id);
    await api.messages.post(opened.channel_id, content);
    await store.reloadDirects();
  };

  /*
   * Поле ветки — то же, что у ленты: права канала ветки на вложения, «@все» и
   * медленный режим — теми же правилами, что в `Chat`.
   */
  const threadPermissions = access?.permissions ?? 0;
  const threadWriting = {
    serverId: place.kind === "server" ? place.id : null,
    canUpload: has(threadPermissions, Permission.UploadFiles),
    canVoiceMessage: has(threadPermissions, Permission.SendVoiceMessages),
    canEveryone: !direct && has(threadPermissions, Permission.MentionEveryone),
    slowExempt:
      has(threadPermissions, Permission.BypassSlowmode) ||
      has(threadPermissions, Permission.ManageChannel) ||
      has(threadPermissions, Permission.ManageMessages),
    stickers: direct ? [] : (state?.stickers ?? []),
    onModerate: moderateFromComposer,
    onDirectMessage: directFromComposer,
  };

  /**
   * Лента канала — посередине или, у голосового, панелью справа от звонка
   * (`docked` — закрыть панель).
   */
  const chatFor = (id: Uuid, docked?: () => void) => (
    <Chat
      docked={docked}
      onEditChannel={
        !direct && has(access?.permissions ?? 0, Permission.ManageChannel)
          ? () => setTuning({ id, deleting: false })
          : undefined
      }
      onCall={
        direct && !docked && !directCall && has(access?.permissions ?? 0, Permission.Connect)
          ? () => joinDirect(direct.channel_id)
          : undefined
      }
      callHint="Начать звонок"
      callStatus={direct ? callStatusOf(direct) : null}
      threads={
        !direct && !docked
          ? (close) => <ThreadList standalone channelId={id} people={peopleById} onClose={close} />
          : undefined
      }
      pins={
        docked
          ? undefined
          : (close) => (
              <>
                <SideHead icon={<IconPinned size={14} />} title="Закреплённые сообщения" onClose={close} />
                <PinnedList
                  key={id}
                  channelId={id}
                  canPin={
                    direct
                      ? has(access?.permissions ?? 0, Permission.SendMessage)
                      : has(access?.permissions ?? 0, Permission.PinMessages)
                  }
                  people={peopleById}
                  custom={customEmoji}
                  channels={state?.channels ?? []}
                  roles={roles}
                  onOpenChannel={setChannelId}
                  onOpenProfile={openProfile}
                  onOpenMessage={openMessage}
                  onOpenServer={openServer}
                />
              </>
            )
      }
      search={
        docked ? undefined : (
          <SearchField
            placeholder={direct ? `Искать в «${directName(direct)}»` : `Искать в «${server?.name ?? "сервере"}»`}
            people={peopleById}
            channels={
              direct
                ? []
                : (state?.channels ?? [])
                    .filter((channel) => !isCall(channel.channel_type))
                    .map((channel) => ({ id: channel.id, name: channel.name }))
            }
            onStart={() => showSide("search")}
            onStop={() => {
              if (side === "search") showSide(null);
            }}
          />
        )
      }
      callStrip={
        direct && !docked && directCall ? (
          <CallStrip
            channelId={direct.channel_id}
            channelName={directName(direct)}
            call={call}
            members={callMembers[direct.channel_id] ?? []}
            ringing={liveRings(store.rings[direct.channel_id], ringNow).map((ring) => ring.user_id)}
            people={[
              ...(isGroup(direct) ? direct.recipients : [direct.recipient]).map(fromPublic),
              ...(me ? [fromSelf(me)] : []),
            ]}
            meId={me?.id ?? ""}
            permissions={access?.permissions ?? 0}
            onJoin={(video) => joinDirect(direct.channel_id, video)}
            onDecline={() => void api.voice.decline(direct.channel_id).catch(() => {})}
            onExpand={() => setDmExpanded(true)}
          />
        ) : null
      }
      stickers={direct ? [] : (state?.stickers ?? [])}
      channelId={id}
      channels={state?.channels ?? []}
      roles={roles}
      myRoles={myRoles}
      onOpenChannel={setChannelId}
      title={direct ? directName(direct) : activeChannel?.name ?? ""}
      topic={direct ? null : activeChannel?.topic ?? null}
      isDirect={!!direct}
      isForum={activeChannel?.channel_type === "forum"}
      isAnnouncement={activeChannel?.channel_type === "announcement"}
      rulesPending={!direct && !!serverRights?.rules_pending}
      verifyUntil={direct ? null : (serverRights?.verify_until ?? null)}
      rules={state?.rules ?? null}
      people={peopleById}
      custom={customEmoji}
      dmsClosed={!!direct && !isGroup(direct) && !!direct.dms_closed}
      blockedWith={
        direct &&
        !isGroup(direct) &&
        (direct.recipient.relationship === "blocked" ||
          direct.recipient.relationship === "blocked_other")
          ? direct.recipient
          : null
      }
      access={access}
      colorOf={colorOf}
      iconOf={iconOf}
      nickOf={nickOf}
      timeoutUntil={myTimeout}
      serverId={place.kind === "server" ? place.id : null}
      openAt={jump?.channelId === channelId ? jump.messageId : null}
      onOpened={() => setJump(null)}
      onOpenMessage={openMessage}
      onOpenServer={openServer}
      onOpenProfile={openProfile}
      onModerate={moderateFromComposer}
      onDirectMessage={directFromComposer}
      onBack={() => setMobileView("nav")}
      side={side}
      /* Участников не бывает в переписке — их там и переключать нечем. */
      hasMembers={place.kind === "server"}
      onSide={(next) => showSide(side === next ? null : next)}
      onUnblock={async (userId) => {
        await api.relationships.unblock(userId);
        await store.reloadDirects();
        await store.reloadRelationships();
      }}
    />
  );

  const page = (
    <div
      className="app"
      data-mobile-view={mobileView}
      style={navWidth ? ({ "--nav-width": `${navWidth}px` } as CSSProperties) : undefined}
    >
      {store.connection === "lost" && (
        <div className="offline-bar" role="status">
          <span className="dot bad" />
          Соединение потеряно. Прочитанное на месте и листается; отправленное встанет в очередь и
          уйдёт само. Попытка {store.attempt}.
        </div>
      )}

      <div className="columns">
        {/*
          Левая сторона — ряд серверов и колонка в верхнем ряду, своя строка
          панелью под ними обоими. Своя строка жила внутри колонки карточкой;
          вынесена наружу потому, что человек — не часть списка каналов: он
          один и тот же и в сервере, и в переписках, а колонка под ним
          меняется целиком.
        */}
        <div className="side">
        <div className="side-cols">
        <Rail
          place={place}
          onGo={go}
          onCreate={() => setCreating(true)}
          menuFor={railMenu}
        />

        {place.kind === "server" ? (
          <aside className="panel nav glass-host">
            {/*
              Вход в настройки один — стрелка рядом с именем. Два значка рядом
              спрашивали бы, чем «приглашения» отличаются от «ролей», хотя
              оба — разделы одного и того же.
            */}
            <MenuOn
              title={`СЕРВЕР · ${(server?.name ?? "").toUpperCase()}`}
              items={withId(serverItems, serverId ?? "")}
              disabled={serverItems.length === 0}
            >
              <button
                type="button"
                className="panel-head panel-head-btn"
                onClick={(event) => setServerMenu({ x: event.clientX, y: event.clientY })}
              >
                <span className="ellipsis">{server?.name ?? "…"}</span>
                <span className="panel-head-more"><IconChevronDown size={14} /></span>
              </button>
            </MenuOn>

            <div className="panel-body">
              {state ? (
                <Boundary area="список каналов" resetKeys={[serverId]}>
                  {asRole?.serverId === state.id && (
                    <div className="as-role-bar">
                      <IconEye size={14} />
                      <span className="grow">
                        Вы смотрите сервер от лица роли <b>{asRole.name}</b>
                      </span>
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => {
                          setAsRole(null);
                          setServerTab("roles");
                        }}
                      >
                        Выйти
                      </button>
                    </div>
                  )}
                  <EventsEntry
                    serverId={state.id}
                    channels={state.channels}
                    meId={me.id}
                    canCreate={
                      !!serverRights &&
                      (serverRights.is_owner ||
                        has(serverRights.permissions, Permission.CreateEvents) ||
                        has(serverRights.permissions, Permission.ManageEvents))
                    }
                    canManageAll={
                      !!serverRights &&
                      (serverRights.is_owner || has(serverRights.permissions, Permission.ManageEvents))
                    }
                    onJoin={(channel) => {
                      setChannelId(channel.id);
                      setPanel("chat");
                      setMobileView("feed");
                      setVoiceChatOf(null);
                      if (call.channelId !== channel.id) void call.join(channel.id);
                    }}
                  />
                  <ChannelList
                    state={
                      asRole?.serverId === state.id
                        ? {
                            ...state,
                            channels: state.channels.filter((item) => asRole.channels.has(item.id)),
                            // Категория без видимых каналов роли не видна вовсе.
                            categories: state.categories.filter((category) =>
                              state.channels.some(
                                (item) => item.category_id === category.id && asRole.channels.has(item.id),
                              ),
                            ),
                          }
                        : state
                    }
                    activeChannelId={channelId}
                    callMembers={callMembers}
                    peopleById={peopleById}
                    unread={store.unread}
                    threads={store.threadUnread}
                    mentions={store.mentions}
                    canManage={
                      !!serverRights &&
                      (serverRights.is_owner ||
                        has(serverRights.permissions, Permission.ManageChannel))
                    }
                    onCreateChannel={(categoryId, kind) => setAdding({ categoryId, kind })}
                    canVoiceStatus={
                      !!serverRights &&
                      (serverRights.is_owner || has(serverRights.permissions, Permission.SetVoiceChannelStatus))
                    }
                    canInvite={
                      !!serverRights &&
                      (serverRights.is_owner || has(serverRights.permissions, Permission.InviteOthers))
                    }
                    onInvite={(channel) => server && setServerDialog({ kind: "invite", server, channel })}
                    voiceInvite={(channel) =>
                      call.state === "live" &&
                      call.channelId === channel.id &&
                      inviteShut !== channel.id &&
                      callRights &&
                      has(callRights.permissions, Permission.InviteOthers) &&
                      server ? (
                        <VoiceInviteRow
                          serverId={state.id}
                          meId={me.id}
                          channelId={channel.id}
                          candidates={inviteCandidates(channel.id)}
                          onShowMore={() => setServerDialog({ kind: "invite", server, channel })}
                          onClose={() => setInviteShut(channel.id)}
                        />
                      ) : null
                    }
                    onCreateCategory={() => server && setServerDialog({ kind: "category", server })}
                    onMarkRead={store.markRead}
                    onSettings={(channel, deleting) => setTuning({ id: channel.id, deleting })}
                    hideMuted={hiddenMuted.has(state.id)}
                    onCategoryPermissions={
                      serverRights &&
                      (serverRights.is_owner || has(serverRights.permissions, Permission.ManagePermissions))
                        ? setCategoryRights
                        : undefined
                    }
                    voiceMenu={(channel, userId) =>
                      userId === me.id
                        ? []
                        : voiceMemberItems({
                            id: userId,
                            permissions: serverRights?.permissions ?? 0,
                            restriction: (() => {
                              // Из состава звонка — он знает точно; список участников — запасной.
                              const inCall = callMembers[channel.id]?.find((item) => item.id === userId);
                              const member = members.find((item) => item.id === userId);
                              return {
                                muted: inCall?.serverMuted ?? !!member?.voice_muted,
                                deafened: inCall?.serverDeafened ?? !!member?.voice_deafened,
                              };
                            })(),
                            moves: state.channels.filter(
                              (item) => isCall(item.channel_type) && item.id !== channel.id,
                            ),
                            onOpenProfile: openProfile,
                            onWriteDirect: (id) => void writeDirect(id),
                            onBan: (id) => {
                              const person = peopleById.get(id);
                              setModerating({ kind: "ban", id, name: person?.display_name || person?.username || "участника" });
                            },
                            onModerate: (action) => {
                              const person = peopleById.get(action.id);
                              void runVoiceAction(
                                state.id,
                                channel.id,
                                action,
                                person?.display_name || person?.username || "участник",
                              ).then(setVoiceRefused);
                            },
                          })
                    }
                    onChanged={() => void store.loadServer(state.id)}
                    onOpenChat={(channel) => {
                      // Чат — без входа в звонок: смотреть звонок и читать можно и снаружи.
                      setProfileId(null);
                      setChannelId(channel.id);
                      setPanel("chat");
                      setMobileView("feed");
                      setVoiceChatOf(channel.id);
                    }}
                    onOpen={(channel) => {
                      setChannelId(channel.id);
                      setPanel("chat");
                      setMobileView("feed");
                      setVoiceChatOf(null);

                      /*
                       * В голосовой канал нажатием и входят.
                       *
                       * Смотреть там нечего — ни ленты, ни истории, — и
                       * «открыть, а потом нажать „Подключиться“» это два шага
                       * к одному и тому же. Кнопка в панели при этом
                       * остаётся: ею возвращаются те, кто из звонка вышел, а
                       * с экрана не ушёл.
                       *
                       * Вход только отсюда, из списка. Тот же канал
                       * открывается и упоминанием через «#», и
                       * восстановлением последнего при запуске — там это была
                       * бы не просьба, а неожиданность.
                       */
                      if (isCall(channel.channel_type) && call.channelId !== channel.id) {
                        void call.join(channel.id);
                      }
                    }}
                  />
                  {voiceRefused && (
                    <button type="button" className="people-note danger-text" onClick={() => setVoiceRefused(null)}>
                      {voiceRefused}
                    </button>
                  )}
                </Boundary>
              ) : (
                <Spinner />
              )}
            </div>

          </aside>
        ) : (
          <aside className="panel panel-wide nav glass-host">
            <header className="panel-head">Переписки</header>

            <div className="panel-body">
              {/* «Найти или начать беседу» — как поле над перепиской в Discord: это тот же быстрый переход. */}
              <button type="button" className="dm-find" onClick={() => setSwitching(true)}>
                Найти или начать беседу
              </button>
              <button
                type="button"
                className={`chan ${panel === "friends" ? "active" : ""}`}
                onClick={() => {
                  setPanel("friends");
                  setChannelId(null);
                  setMobileView("feed");
                }}
              >
                <span className="chan-sigil"><IconMenu size={14} /></span>
                <span className="chan-name">Друзья</span>
                <span className="badge-slot" />
              </button>

              <div className="rule" style={{ margin: "8px 0" }} />

              {store.directs.length === 0 && (
                <div className="small muted" style={{ padding: "8px 10px" }}>
                  Переписок нет. Открываются они из профиля или списка друзей — списка всех людей
                  нет по замыслу.
                </div>
              )}

              <button
                type="button"
                className="chan"
                onClick={() => setGrouping(true)}
              >
                <span className="chan-sigil"><IconPlus size={14} /></span>
                <span className="chan-name">Новая группа</span>
                <span className="badge-slot" />
              </button>

              {store.directs.map((item) => {
                if (isGroup(item)) {
                  const fresh = (store.unread[item.channel_id] ?? 0) > 0;
                  const level = store.notifyChannel[item.channel_id] ?? null;
                  const typing = store.typingIn(item.channel_id).some((id) => id !== me.id);
                  const name = directName(item);
                  const groupItems: MenuItem[] = [
                    { label: "Отметить как прочитанное", onPick: () => store.markRead(item.channel_id) },
                    {
                      label: "Уведомления",
                      hint: level ? levelLabel(level) : "Все сообщения",
                      items: (["all", "mentions", "none"] as const).map((value) => ({
                        label: levelLabel(value),
                        choice: true,
                        on: (level ?? "all") === value,
                        onPick: () => void store.setChannelNotify(item.channel_id, value),
                      })),
                    },
                    { label: "Переименовать", onPick: () => setRenamingGroup(item) },
                    // Как в Discord: из звонка — позвать тех, кого в нём нет.
                    ...(call.state === "live" && call.channelId === item.channel_id
                      ? [{ label: "Позвать в звонок", onPick: () => void api.voice.ring(item.channel_id).catch(() => {}) }]
                      : []),
                    SEPARATOR,
                    {
                      label: "Покинуть группу",
                      kind: "danger",
                      onPick: () =>
                        void api.directs
                          .removeFromGroup(item.channel_id, me.id)
                          .then(() => store.reloadDirects())
                          .catch((error: unknown) =>
                            setVoiceRefused(error instanceof ApiError ? error.message : "Не вышло покинуть группу"),
                          ),
                    },
                  ];

                  return (
                    <MenuOn
                      key={item.channel_id}
                      title={name}
                      sub={`${item.recipients.length + 1} ${plural(item.recipients.length + 1, "участник", "участника", "участников")}`}
                      items={withId(groupItems, item.channel_id)}
                    >
                      <button
                        type="button"
                        className={`chan ${channelId === item.channel_id ? "active" : ""} ${fresh ? "unread" : ""}`}
                        style={{ gap: 9, padding: "7px 8px" }}
                        onClick={() => {
                          setChannelId(item.channel_id);
                          rememberDirect(item.channel_id);
                          setPanel("chat");
                          setMobileView("feed");
                        }}
                      >
                        {fresh && <span className="chan-notch" />}
                        <GroupTile id={item.channel_id} size={30} iconId={item.icon_id} />
                        <span className="chan-name dm-name">
                          <span className="ellipsis">{name}</span>
                          {callStatusOf(item) && (
                            <span className="dm-call">
                              <IconCall size={11} /> {callStatusOf(item)}
                            </span>
                          )}
                        </span>
                        {typing ? (
                          <span className="dm-typing" aria-label="печатает">
                            <i />
                            <i />
                            <i />
                          </span>
                        ) : fresh ? (
                          <span className={`badge ${store.mentions[item.channel_id] ? "" : "quiet"}`}>
                            {tally(store.unread[item.channel_id], store.mentions[item.channel_id] ?? 0)}
                          </span>
                        ) : (
                          <span className="badge-slot" />
                        )}
                      </button>
                    </MenuOn>
                  );
                }

                const person = {
                  id: item.recipient.id,
                  name: item.recipient.display_name || item.recipient.username,
                  handle: item.recipient.handle,
                  avatarId: item.recipient.avatar_id,
                  decorationId: item.recipient.decoration_id ?? null,
                  presence: item.recipient.status_presence,
                  online: item.recipient.online,
                };
                const blocked =
                  item.recipient.relationship === "blocked" ||
                  item.recipient.relationship === "blocked_other";
                /*
                 * Засечка есть и у открытой переписки — по тому же правилу,
                 * что и в списке каналов: счёт идёт по увиденному, и число
                 * остаётся ровно тогда, когда конец ленты за нижним краем.
                 */
                const fresh = (store.unread[item.channel_id] ?? 0) > 0;
                const level = store.notifyChannel[item.channel_id] ?? null;
                // Собеседник печатает — точки на месте значка, как в Discord.
                const typing = store.typingIn(item.channel_id).some((id) => id === item.recipient.id);

                /*
                 * Меню переписки по правой кнопке — как в Discord. Закрыть
                 * переписку здесь нельзя: скрытие должно ехать на все
                 * устройства, а хранить его негде, кроме сервера.
                 */
                // Позвать — из своего звонка, если собеседника в нём нет и ему ещё не звонят.
                const canRing =
                  call.state === "live" &&
                  call.channelId === item.channel_id &&
                  !(callMembers[item.channel_id] ?? []).some((member) => member.id === item.recipient.id) &&
                  !liveRings(store.rings[item.channel_id], ringNow).some((ring) => ring.user_id === item.recipient.id);
                const directItems: MenuItem[] = [
                  { label: "Профиль", onPick: () => openFull(item.recipient.id) },
                  ...(canRing
                    ? [
                        {
                          label: "Позвать в звонок",
                          onPick: () => void api.voice.ring(item.channel_id, [item.recipient.id]).catch(() => {}),
                        },
                      ]
                    : []),
                  { label: "Отметить как прочитанное", onPick: () => store.markRead(item.channel_id) },
                  {
                    label: "Уведомления",
                    hint: level ? levelLabel(level) : "Все сообщения",
                    items: (["all", "mentions", "none"] as const).map((value) => ({
                      label: levelLabel(value),
                      choice: true,
                      on: (level ?? "all") === value,
                      onPick: () => void store.setChannelNotify(item.channel_id, value),
                    })),
                  },
                  {
                    label: "Закрыть переписку",
                    onPick: () =>
                      void api.directs
                        .close(item.channel_id)
                        .then(() => {
                          if (channelId === item.channel_id) setChannelId(null);
                          return store.reloadDirects();
                        })
                        .catch(() => {}),
                  },
                  SEPARATOR,
                  {
                    label: "Копировать хэндл",
                    hint: item.recipient.handle,
                    onPick: () => void navigator.clipboard?.writeText(item.recipient.handle).catch(() => {}),
                  },
                  item.recipient.relationship === "blocked"
                    ? {
                        label: "Разблокировать",
                        onPick: () =>
                          void api.relationships
                            .unblock(item.recipient.id)
                            .then(() => Promise.all([store.reloadDirects(), store.reloadRelationships()])),
                      }
                    : {
                        label: "Заблокировать",
                        kind: "danger",
                        onPick: () =>
                          void api.relationships
                            .block(item.recipient.id)
                            .then(() => Promise.all([store.reloadDirects(), store.reloadRelationships()])),
                      },
                ];

                return (
                  <MenuOn key={item.channel_id} title={person.name} sub={item.recipient.handle} items={withId(directItems, item.recipient.id)}>
                  <button
                    type="button"
                    className={`chan ${channelId === item.channel_id ? "active" : ""} ${
                      fresh ? "unread" : ""
                    }`}
                    style={{ opacity: blocked ? 0.5 : 1, gap: 9, padding: "7px 8px" }}
                    onClick={() => {
                      setChannelId(item.channel_id);
                      rememberDirect(item.channel_id);
                      setPanel("chat");
                      setMobileView("feed");
                    }}
                  >
                    {fresh && <span className="chan-notch" />}
                    <Tile person={person} size={30} />
                    <span className="chan-name dm-name">
                      <span className="ellipsis">{person.name}</span>
                      {callStatusOf(item) && (
                        <span className="dm-call">
                          <IconCall size={11} /> {callStatusOf(item)}
                        </span>
                      )}
                    </span>
                    {blocked ? (
                      <span className="tiny muted"><IconMicOff size={13} /></span>
                    ) : typing ? (
                      <span className="dm-typing" aria-label="печатает">
                        <i />
                        <i />
                        <i />
                      </span>
                    ) : fresh ? (
                      <span className={`badge ${store.mentions[item.channel_id] ? "" : "quiet"}`}>
                        {tally(store.unread[item.channel_id], store.mentions[item.channel_id] ?? 0)}
                      </span>
                    ) : (
                      <span className="badge-slot" />
                    )}
                  </button>
                  </MenuOn>
                );
              })}
            </div>

          </aside>
        )}
        </div>

        <CallDock
          call={call}
          channelName={callAt?.channel ?? ""}
          serverName={callAt?.server ?? ""}
          permissions={callRights?.permissions ?? 0}
          serverId={callAt?.serverId ?? null}
          onOpen={() => {
            if (!call.channelId) return;
            if (callAt?.serverId) setPlace({ kind: "server", id: callAt.serverId });
            else if (store.directs.some((item) => item.channel_id === call.channelId)) setPlace({ kind: "directs" });
            setPanel("chat");
            setChannelId(call.channelId);
            setMobileView("feed");
          }}
        />

        <SelfBar
          call={call}
          onSettings={(open) => {
            setSettingsAt(open ?? "account");
          }}
          onOpenProfile={openFull}
        />
        </div>

        {/* Шов один на обе колонки: слева от него всегда та, что выбрана выше. */}
        <NavGrip />

        {panel === "friends" && (
          <Boundary area="друзья">
            <Suspense fallback={<Loading />}>
              <Friends onOpenDirect={openDirect} />
            </Suspense>
          </Boundary>
        )}

        {/*
          Пока едут права канала, место держит заглушка, а не пустота.
          Раньше здесь стояло `&& access`, и до ответа сервера середины не
          было вовсе: ряд колонок схлопывался, участники прыгали влево и
          обратно. Права нужны по-настоящему — по ним решается, есть ли
          микрофон и показ, — но ждать их надо на своём месте.
        */}
        {panel === "chat" && !!activeChannel && isCall(activeChannel.channel_type) && !access && <Loading />}

        {panel === "chat" && !!activeChannel && isCall(activeChannel.channel_type) && access && (
          <Boundary area="голосовой канал" resetKeys={[activeChannel.id]}>
            <Suspense fallback={<Loading />}>
              <Voice
                channel={activeChannel}
                serverName={server?.name ?? ""}
                permissions={access.permissions}
                peopleById={peopleById}
                members={members}
                voiceChannels={(state?.channels ?? []).filter((item) => isCall(item.channel_type))}
                chatOpen={voiceChat}
                onOpenChat={() => setVoiceChatOf(voiceChat ? null : activeChannel.id)}
                call={call}
                onLeaveView={() => setMobileView("nav")}
                onOpenProfile={openProfile}
                onWriteDirect={(id) => void writeDirect(id)}
                onBan={(id) => {
                  const person = peopleById.get(id);
                  setModerating({ kind: "ban", id, name: person?.display_name || person?.username || "участника" });
                }}
              />
            </Suspense>
          </Boundary>
        )}

        {panel === "chat" && channelId && !isCall(activeChannel?.channel_type ?? "text") && gated && (
          <AgeGate
            name={activeChannel?.name ?? ""}
            onContinue={() => setAgeOk(confirmAge(channelId))}
            onLeave={() => setChannelId(null)}
          />
        )}

        {/*
          Чат голосового канала — панелью справа от звонка, как в Discord:
          звонок остаётся на виду, место участников занимает лента.
        */}
        {panel === "chat" && channelId && !!activeChannel && isCall(activeChannel.channel_type) && voiceChat && (
          <aside className="panel panel-wide glass-host voice-dock">
            {gated ? (
              <AgeGate
                name={activeChannel.name}
                onContinue={() => setAgeOk(confirmAge(channelId))}
                onLeave={() => setVoiceChatOf(null)}
              />
            ) : (
              chatFor(channelId, () => setVoiceChatOf(null))
            )}
          </aside>
        )}

        {panel === "chat" &&
          channelId &&
          !isCall(activeChannel?.channel_type ?? "text") &&
          !gated &&
          !dmStage &&
          (fullThread ? (
            <Boundary area="ветка">
              <ThreadPanel
                full
                membersOpen={threadPeople && side !== "search"}
                onToggleMembers={() => {
                  // Поиск занимает ту же колонку: значок участников возвращает их на его место.
                  if (side === "search") {
                    showSide(null);
                    setThreadPeople(true);
                  } else setThreadPeople(!threadPeople);
                }}
                search={
                  <SearchField
                    placeholder={`Искать в «${server?.name ?? "сервере"}»`}
                    people={peopleById}
                    channels={(state?.channels ?? [])
                      .filter((channel) => !isCall(channel.channel_type))
                      .map((channel) => ({ id: channel.id, name: channel.name }))}
                    onStart={() => showSide("search")}
                    onStop={() => {
                      if (side === "search") showSide(null);
                    }}
                  />
                }
                people={peopleById}
                custom={customEmoji}
                channels={state?.channels ?? []}
                roles={roles}
                myRoles={myRoles}
                onOpenProfile={openProfile}
                onOpenChannel={setChannelId}
                canSend={has(access?.permissions ?? 0, Permission.SendMessagesInThreads)}
                canReact={has(access?.permissions ?? 0, Permission.React)}
                canDeleteOthers={has(access?.permissions ?? 0, Permission.ManageMessages)}
                canPin={has(access?.permissions ?? 0, Permission.PinMessages)}
                canManageThreads={has(access?.permissions ?? 0, Permission.ManageThreads)}
                {...threadWriting}
              />
            </Boundary>
          ) : (
            chatFor(channelId)
          ))}

        {panel === "chat" && direct && dmStage && directChannel && access && (
          <Boundary area="звонок" resetKeys={[direct.channel_id]}>
            <Suspense fallback={<Loading />}>
              <Voice
                channel={directChannel}
                serverName={isGroup(direct) ? "группа" : "личная переписка"}
                permissions={access.permissions}
                peopleById={peopleById}
                members={[]}
                voiceChannels={[]}
                chatOpen={!dmChatHidden}
                onOpenChat={() => setDmChatHidden((hidden) => !hidden)}
                call={call}
                onLeaveView={() => setMobileView("nav")}
                onOpenProfile={openProfile}
                onWriteDirect={(id) => void writeDirect(id)}
                onBan={() => {}}
                onCollapse={() => setDmExpanded(false)}
              />
            </Suspense>
          </Boundary>
        )}

        {panel === "chat" && direct && dmStage && !dmChatHidden && (
          <aside className="panel panel-wide glass-host voice-dock">
            {chatFor(direct.channel_id, () => setDmChatHidden(true))}
          </aside>
        )}

        {panel === "chat" && !channelId && (
          <section className="sheet pane-surface">
            <div className="empties">
              {place.kind === "server" ? (
                <Empty
                  title="Канал не выбран"
                  body="Слева — каналы сервера. Текстовые открываются лентой, голосовые — звонком."
                />
              ) : store.directs.length > 0 ? (
                <Empty
                  title="Переписка не выбрана"
                  body="Слева — ваши переписки. Новая открывается из профиля или списка друзей."
                  action="Открыть друзей"
                  onAction={() => setPanel("friends")}
                />
              ) : (
                <Empty
                  title="Переписок нет"
                  body="Переписка открывается из профиля или списка друзей. Списка всех людей нет по замыслу."
                  action="Открыть друзей"
                  onAction={() => setPanel("friends")}
                />
              )}
            </div>
          </section>
        )}

        {/*
          Ветка занимает место участников, а не добавляет колонку: раскладка
          остаётся четырёхколоночной, как и была.
        */}
        {panel === "chat" && !voiceChat && side === "threads" && (
          <Boundary area="панель веток">
            <ThreadPanel
              people={peopleById}
              custom={customEmoji}
              channels={state?.channels ?? []}
              roles={roles}
              myRoles={myRoles}
              onOpenProfile={openProfile}
              onOpenChannel={setChannelId}
              canSend={has(access?.permissions ?? 0, Permission.SendMessagesInThreads)}
              canReact={has(access?.permissions ?? 0, Permission.React)}
              canDeleteOthers={has(access?.permissions ?? 0, Permission.ManageMessages)}
              canPin={has(access?.permissions ?? 0, Permission.PinMessages)}
              canManageThreads={has(access?.permissions ?? 0, Permission.ManageThreads)}
              {...threadWriting}
            />
          </Boundary>
        )}

        {panel === "chat" && !voiceChat && side === "search" && channelId && (
          <aside className="side-col side-col-wide glass-host">
            <SideHead
              icon={<IconSearch size={14} />}
              title="Поиск"
              onClose={() => {
                useSearch.getState().clear();
                showSide(null);
              }}
            />
            <SearchResults
              channelId={channelId}
              serverId={place.kind === "server" ? place.id : null}
              channelName={(id) => state?.channels.find((channel) => channel.id === id)?.name ?? null}
              people={peopleById}
              onOpen={(message) =>
                openMessage({
                  serverId: place.kind === "server" ? place.id : null,
                  channelId: message.channel_id,
                  messageId: message.id,
                })
              }
            />
          </aside>
        )}

        {/*
          Участники — только у текстового канала. В голосовом их не бывает
          никогда, как в Discord: кто в звонке, видно в самом звонке и в
          списке каналов, а колонка, оставшаяся открытой из текстового,
          отнимала у звонка место.
        */}
        {/* Справа у ветки на весь экран — кто в ней, как в Discord: если колонка не занята поиском. */}
        {panel === "chat" && fullThread?.threadId && threadPeople && side !== "search" && (
          <ThreadMembers
            channelId={fullThread.channelId}
            threadId={fullThread.threadId}
            people={peopleById}
            onOpenProfile={openProfile}
          />
        )}

        {panel === "chat" &&
          !voiceChat &&
          !fullThread &&
          side === "members" &&
          !isCall(activeChannel?.channel_type ?? "text") &&
          place.kind === "server" &&
          state && (
          <MemberColumn
            serverId={serverId!}
            channelId={channelId}
            members={members}
            roles={roles}
            rights={serverRights}
            meId={me?.id ?? null}
            onOpenProfile={openProfile}
            onWriteDirect={(id) => void writeDirect(id)}
            onInvite={(id, target) => void inviteTo(id, target)}
            onModerate={setModerating}
            onClose={() => showSide(null)}
          />
        )}

        {/* В переписке правая колонка занята собеседником: списка участников
            у неё нет, а место есть, и профиль там нужнее всего. */}
        {panel === "chat" && direct && isGroup(direct) && !dmStage && (
          <aside className="panel panel-wide glass-host">
            <header className="panel-head">Группа</header>
            <Boundary area="участники группы">
              <GroupPanel
                group={direct}
                me={{ ...me, relationship: "none", online: true } as PublicUser}
                friends={store.relationships.filter((user) => user.relationship === "friend")}
                onOpenProfile={openProfile}
                onLeft={() => {
                  void store.reloadDirects();
                  setChannelId(null);
                  setPanel("friends");
                }}
              />
            </Boundary>
          </aside>
        )}

        {panel === "chat" && direct && !isGroup(direct) && !dmStage && (
          // Как в Discord: панель профиля без своей шапки, шире колонки участников (`panel-profile`).
          <aside className="panel panel-wide panel-profile glass-host">
            <Boundary area="профиль">
              <Suspense fallback={<Loading />}>
                <Profile
                  key={direct.recipient.id}
                  userId={direct.recipient.id}
                  onChanged={() => {
                    void store.reloadRelationships();
                    void store.reloadDirects();
                  }}
                  onOpenFull={openFull}
                />
              </Suspense>
            </Boundary>
          </aside>
        )}
      </div>

      {profileId && (
        <Boundary area="профиль">
          <Suspense fallback={null}>
            <ProfileModal
              userId={profileId}
              tab={profileTab}
              notice={profileNotice?.id === profileId ? profileNotice.text : null}
              server={profileServer(profileId)}
              onClose={() => setProfileId(null)}
              onWrite={async (user) => {
                const opened = await api.directs.open(user.id);
                await store.reloadDirects();
                openDirect(opened.channel_id);
                setProfileId(null);
              }}
              onOpenProfile={openFull}
              onOpenServer={(id) => {
                setProfileId(null);
                go({ kind: "server", id });
              }}
              onEditProfile={() => {
                setProfileId(null);
                setSettingsAt("profile");
              }}
              onChanged={() => void store.reloadRelationships()}
            />
          </Suspense>
        </Boundary>
      )}
      {popout && (
        <Boundary area="карточка профиля">
          <Suspense fallback={null}>
            <ProfilePopout
              key={popout.id}
              userId={popout.id}
              anchor={popout.anchor}
              server={profileServer(popout.id)}
              onClose={() => setPopout(null)}
              onOpenFull={openFull}
              onSend={sendDirect}
              onCall={(user) => void callPerson(user.id)}
              onEditProfile={() => {
                setPopout(null);
                setSettingsAt("profile");
              }}
              onChanged={() => void store.reloadRelationships()}
            />
          </Suspense>
        </Boundary>
      )}

      {/* Звук идёт, даже когда экран звонка закрыт и панель убрана. */}
      <CallAudio call={call} />
      <MediaViewerHost />
      {me && (
        <IncomingCall
          directs={store.directs}
          rings={store.rings}
          now={ringNow}
          meId={me.id}
          callChannelId={call.state === "live" || call.state === "joining" ? call.channelId : null}
          openChannelId={panel === "chat" && direct ? direct.channel_id : null}
          onDecline={(id) => void api.voice.decline(id).catch(() => {})}
          onAccept={(id) => {
            joinDirect(id);
            setPlace({ kind: "directs" });
            setChannelId(id);
            rememberDirect(id);
            setPanel("chat");
            setMobileView("feed");
          }}
        />
      )}

      {grouping && (
        <PickFriends
          title="Новая группа"
          action="Создать группу"
          withName
          friends={store.relationships.filter((user) => user.relationship === "friend")}
          room={GROUP_MAX - 1}
          onPick={async (ids, name) => {
            const created = await api.directs.createGroup(ids, name);
            await store.reloadDirects();
            setPlace({ kind: "directs" });
            setChannelId(created.channel_id);
            rememberDirect(created.channel_id);
            setPanel("chat");
            setMobileView("feed");
          }}
          onClose={() => setGrouping(false)}
        />
      )}
      {renamingGroup && <RenameGroup group={renamingGroup} onClose={() => setRenamingGroup(null)} />}

      {/*
        Настройки — поверх приложения, а не вместо него: канал остаётся на
        месте, звонок не рвётся, закрытие возвращает ровно туда, откуда вошли.

        Настройки человека жили здесь особняком — отдельным экраном вместо
        всего остального. Разницы по существу между ними и настройками сервера
        нет, а стоила она того, что за спиной у настроек не оставалось ничего:
        открывший их из переписки возвращался не в неё, а в начало.
      */}
      {settingsAt && (
        <Boundary area="настройки">
          <Suspense fallback={<Loading over />}>
            <Settings
              open={settingsAt}
              onClose={() => setSettingsAt(null)}
              onDevelopers={() => {
                setSettingsAt(null);
                setDevelopers(true);
              }}
              theme={theme}
              onTheme={changeTheme}
              wall={wall}
              onWall={changeWall}
            />
          </Suspense>
        </Boundary>
      )}
      {developers && (
        <Boundary area="портал разработчика">
          <Suspense fallback={null}>
            <Portal onClose={() => setDevelopers(false)} />
          </Suspense>
        </Boundary>
      )}
      {botInvite && (
        <Boundary area="приглашение бота">
          <Suspense fallback={null}>
            <AuthorizeBot
              clientId={botInvite.clientId}
              permissions={botInvite.permissions}
              servers={store.servers.map((server) => ({ id: server.id, name: server.name }))}
              onClose={() => setBotInvite(null)}
              onDone={(target) => {
                setBotInvite(null);
                go({ kind: "server", id: target });
              }}
            />
          </Suspense>
        </Boundary>
      )}

      {serverTab && state && serverRights && (
        <Boundary area="настройки сервера">
          <Suspense fallback={<Loading over />}>
            <ServerSettings
              state={state}
              mine={serverRights}
              meId={me.id}
              start={serverTab}
              onClose={() => setServerTab(null)}
              /*
               * Оба списка: имя, значок и описание живут в списке серверов, а
               * каналы и категории — в состоянии открытого. Правка настроек
               * задевает и то и другое, а перечитывалось только второе.
               */
              onChanged={() => {
                void store.loadServer(state.id);
                void store.reloadServers();
              }}
              onGone={() => {
                setServerTab(null);
                setPlace({ kind: "directs" });
                setChannelId(null);
                void store.reloadServers();
              }}
              onPreviewRole={(role) =>
                void api.roles
                  .preview(state.id, role.is_default ? [] : [role.id])
                  .then((preview) =>
                    setAsRole({
                      serverId: state.id,
                      name: role.is_default ? "@все" : role.name,
                      channels: new Set(preview.channel_ids),
                    }),
                  )
                  .catch(() => {})
              }
            />
          </Suspense>
        </Boundary>
      )}

      {categoryRights && state && serverRights && (
        <SettingsModal
          title="Права категории"
          subtitle={categoryRights.name}
          size="wide"
          onClose={() => setCategoryRights(null)}
        >
          <Suspense fallback={<Loading />}>
            <Roles
              serverId={state.id}
              channels={state.channels}
              mine={serverRights}
              scopeCategory={{ id: categoryRights.id, name: categoryRights.name }}
            />
          </Suspense>
        </SettingsModal>
      )}

      {tuning && serverId && state && serverRights && (
        <Boundary area="настройки канала">
          <Suspense fallback={<Loading over />}>
            <ChannelSettings
              channel={state.channels.find((item) => item.id === tuning.id) ?? state.channels[0]}
              deleting={tuning.deleting}
              categories={state.categories}
              serverId={serverId}
              serverName={server?.name ?? ""}
              channels={state.channels}
              mine={serverRights}
              onClose={() => setTuning(null)}
              onChanged={() => void store.loadServer(serverId)}
              onDeleted={() => {
                setTuning(null);
                void store.loadServer(serverId);
              }}
            />
          </Suspense>
        </Boundary>
      )}

      {removed && (
        <Dialog
          title={removed.banned ? "Вас забанили" : "Вас исключили"}
          onClose={() => store.setRemovedFrom(null)}
          actions={
            <Button size="sm" onClick={() => store.setRemovedFrom(null)}>
              Понятно
            </Button>
          }
        >
          <Notice kind="bad" title={removed.banned ? "Вход закрыт" : "Вернуться можно"}>
            {removed.banned
              ? "Сервер закрыт для вас: по приглашению туда больше не войти."
              : "Сервер больше не ваш, но если позовут снова — войдёте по приглашению."}
          </Notice>
        </Dialog>
      )}

      {serverMenu && state && (
        <Menu
          title={`СЕРВЕР · ${state.name.toUpperCase()}`}
          at={serverMenu}
          onClose={() => setServerMenu(null)}
          items={withId(serverItems, serverId ?? "")}
        />
      )}

      {serverDialog?.kind === "invite" && (() => {
        const target =
          serverDialog.channel ?? (serverDialog.server.id === serverId && activeChannel ? activeChannel : null);
        const voice = !!target && isCall(target.channel_type);
        return (
          <InviteDialog
            serverId={serverDialog.server.id}
            serverName={serverDialog.server.name}
            meId={me.id}
            channel={target ? { id: target.id, name: target.name } : null}
            friends={voice ? inviteContacts : store.relationships.filter((user) => user.relationship === "friend")}
            members={new Set(serverDialog.server.id === serverId ? members.map((member) => member.id) : [])}
            voice={voice}
            inCall={new Set((target ? callMembers[target.id] : undefined)?.map((member) => member.id) ?? [])}
            voiceOf={voiceChannelOf}
            onClose={() => setServerDialog(null)}
          />
        );
      })()}
      {serverDialog?.kind === "category" && (
        <Boundary area="новая категория">
          <Suspense fallback={<Loading over />}>
            <CategoryNew
              serverId={serverDialog.server.id}
              onClose={() => setServerDialog(null)}
              onCreated={() => {
                void store.loadServer(serverDialog.server.id);
                setServerDialog(null);
              }}
            />
          </Suspense>
        </Boundary>
      )}
      {serverDialog?.kind === "event" && state && state.id === serverDialog.server.id && (
        <NewEventDialog serverId={state.id} channels={state.channels} onClose={() => setServerDialog(null)} />
      )}
      {serverDialog?.kind === "notify" && (
        <NotifyDialog
          serverName={serverDialog.server.name}
          level={
            store.notifyServer[serverDialog.server.id] ??
            store.states[serverDialog.server.id]?.default_notify ??
            serverDialog.server.default_notify ??
            "all"
          }
          onPick={(level) => void store.setServerNotify(serverDialog.server.id, level)}
          onClose={() => setServerDialog(null)}
        />
      )}
      {serverDialog?.kind === "privacy" && (
        <PrivacyDialog serverName={serverDialog.server.name} onClose={() => setServerDialog(null)} />
      )}

      {adding && serverId && state && (
        <Boundary area="новый канал">
          <Suspense fallback={<Loading over />}>
            <ChannelNew
              serverId={serverId}
              categories={state.categories}
              categoryId={adding.categoryId}
              initialKind={adding.kind}
              onClose={() => setAdding(null)}
              onCreated={(channel) => {
                setAdding(null);
                void store.loadServer(serverId);
                setChannelId(channel.id);
                setPanel("chat");
                setMobileView("feed");
              }}
            />
          </Suspense>
        </Boundary>
      )}

      {switching && <QuickSwitcher onPick={switchTo} onClose={() => setSwitching(false)} />}
      {inbox && (
        <Inbox
          renderMessage={(message) => (
            <MessagePreview
              message={message}
              people={peopleById}
              onOpenChannel={(id) => {
                setInbox(false);
                openMessage({ serverId: store.serverOf(id) ?? null, channelId: id, messageId: null });
              }}
              onOpenMessage={(link) => {
                setInbox(false);
                openMessage(link);
              }}
            />
          )}
          onClose={() => setInbox(false)}
          onOpen={(item) => {
            setInbox(false);
            openMessage({
              serverId: item.server_id,
              channelId: item.message.channel_id,
              messageId: item.message.id,
            });
          }}
          onOpenChannel={(serverId, channelId) => {
            setInbox(false);
            openMessage({ serverId, channelId, messageId: null });
          }}
        />
      )}

      {moderating && serverId && moderating.kind === "ban" && (
        <BanDialog
          serverId={serverId}
          user={moderating}
          onClose={() => setModerating(null)}
        />
      )}
      {moderating && serverId && moderating.kind === "nickname" && (
        <NicknameDialog
          serverId={serverId}
          user={moderating}
          onClose={() => setModerating(null)}
        />
      )}
      {moderating && serverId && moderating.kind === "kick" && (
        <KickDialog
          serverId={serverId}
          user={moderating}
          onClose={() => setModerating(null)}
        />
      )}

      {shortcuts && <Shortcuts onClose={() => setShortcuts(false)} />}
      {/* Окно бота (ответ 9) — одно на приложение: вызов мог прийти из ленты, ветки или меню. */}
      <BotModalHost />
      <WelcomeGate
        serverId={serverId}
        serverName={state?.name}
        channels={state?.channels}
        onOpenChannel={(id) => {
          setPanel("chat");
          setChannelId(id);
          setMobileView("feed");
        }}
      />
      <LinkGuard />

      {creating && (
        <CreateOrJoin
          canCreate={me.privileged}
          onClose={() => setCreating(false)}
          onDone={async (id, channel) => {
            setCreating(false);
            await store.reloadServers();
            await refreshMe();
            setPlace({ kind: "server", id });
            setPanel("chat");
            if (channel) setChannelId(channel);
          }}
        />
      )}
    </div>
  );

  return <LinkPlacesProvider value={linkPlaces}>{page}</LinkPlacesProvider>;
}

function Chat({
  channelId,
  title,
  topic,
  isDirect,
  isForum = false,
  isAnnouncement = false,
  rulesPending = false,
  verifyUntil = null,
  rules = null,
  blockedWith,
  dmsClosed = false,
  access,
  people,
  custom,
  channels,
  roles,
  myRoles,
  colorOf,
  iconOf,
  nickOf,
  timeoutUntil,
  serverId,
  openAt,
  onOpened,
  onOpenMessage,
  onOpenServer,
  onOpenProfile,
  onOpenChannel,
  onModerate,
  onDirectMessage,
  onBack,
  onUnblock,
  side,
  hasMembers,
  onSide,
  onCall,
  callHint,
  callStatus = null,
  callStrip = null,
  threads,
  pins,
  search,
  stickers,
  docked,
  onEditChannel,
}: {
  channelId: Uuid;
  /** Чат голосового канала панелью справа от звонка: закрыть панель. */
  docked?: () => void;
  /** «Настроить канал» в приветствии начала истории; нет — нет права. */
  onEditChannel?: () => void;
  /** Стикеры сервера — для выбора у поля ввода. В переписке их нет. */
  stickers: ServerSticker[];
  /** Чат голосового канала: вернуться к звонку; переписки — начать звонок. */
  onCall?: () => void;
  callHint?: string;
  /** «Участвует в звонке» рядом с именем переписки; `null` — звонка без нас нет. */
  callStatus?: string | null;
  /** Полоса звонка переписки — над лентой, под шапкой. */
  callStrip?: ReactNode;
  /** Окна веток и закреплённых под значками шапки, поле поиска в её конце. */
  threads?: (close: () => void) => ReactNode;
  pins?: (close: () => void) => ReactNode;
  search?: ReactNode;
  /** Цвет имени по старшей цветной роли; `null` — обычный. */
  colorOf: (userId: Uuid) => string | null;
  iconOf: (userId: Uuid) => string | null;
  /** Ник на сервере; `null` — общее имя. */
  nickOf: (userId: Uuid) => string | null;
  /** Мой тайм-аут на этом сервере — до какого времени; `null` — нет. */
  timeoutUntil: string | null;
  /** Чей канал — для ссылок на сообщения. `null` — личная переписка. */
  serverId: Uuid | null;
  /** Сообщение, к которому привела ссылка: лента откроется вокруг него. */
  openAt: Uuid | null;
  /** Лента взяла `openAt` — второй раз туда не вести. */
  onOpened: () => void;
  onOpenMessage: (link: MessageLink) => void;
  /** Открыть сервер (и канал) — после вступления по карточке приглашения. */
  onOpenServer: (serverId: Uuid, channelId: Uuid | null) => void;
  title: string;
  /** Тема канала — в шапке рядом с именем. У переписки её нет. */
  topic: string | null;
  isDirect: boolean;
  /** Правила сервера ещё не приняты: вместо поля ввода — полоса «примите правила». */
  rulesPending?: boolean;
  /** До какого времени новичок только читает — уровень проверки сервера. */
  verifyUntil?: string | null;
  rules?: string | null;
  /** Форум: вместо ленты и поля ввода — посты. */
  isForum?: boolean;
  /** Канал объявлений: в шапке «Подписаться», у сообщений — «Опубликовать». */
  isAnnouncement?: boolean;
  blockedWith: PublicUser | null;
  /** Собеседник принимает личные сообщения только от друзей, а мы ему не друг. */
  dmsClosed?: boolean;
  access: Access | null;
  /** Кого знаем по имени: из них собираются имена для упоминаний. */
  people: Map<Uuid, Named>;
  /** Знаки этого сервера. В личной переписке пусто. */
  custom: CustomEmoji[];
  /** Каналы этого сервера: их называют через «#» и на них нажимают. */
  channels: Channel[];
  /** Роли этого сервера: их называют через «@» рядом с людьми. */
  roles: Role[];
  /** Свои роли: по ним видно, что через роль позвали нас. */
  myRoles: Uuid[];
  onOpenChannel: (channelId: Uuid) => void;
  /** Профиль: с опорой — карточка рядом с ней, без — полное окно. */
  onOpenProfile: (id: Uuid, anchor?: HTMLElement) => void;
  /** «/kick» и «/ban» из поля ввода — то же окно, что из меню человека. */
  onModerate: (kind: "kick" | "ban", id: Uuid) => void;
  /** «/msg» — написать в личку, не уходя из канала. */
  onDirectMessage: (id: Uuid, content: string) => Promise<void>;
  onBack: () => void;
  onUnblock: (userId: Uuid) => Promise<void>;
  /** Что стоит в правой колонке сейчас. Нажатым рисуется ровно этот значок. */
  side: Side | null;
  /** Бывают ли здесь участники: в переписке их нет, и значка тоже. */
  hasMembers: boolean;
  onSide: (next: Side) => void;
}) {
  const permissions = access?.permissions ?? 0;
  const canSend = has(permissions, Permission.SendMessage);
  // Медленный режим держит рядовых; ведущих канал и обходящих — нет, как в Discord.
  const slowExempt =
    has(permissions, Permission.BypassSlowmode) ||
    has(permissions, Permission.ManageChannel) ||
    has(permissions, Permission.ManageMessages);
  const slowmode = slowExempt ? 0 : channels.find((channel) => channel.id === channelId)?.slowmode ?? 0;
  const canUpload = has(permissions, Permission.UploadFiles);
  const canDeleteOthers = has(permissions, Permission.ManageMessages);
  const canReact = has(permissions, Permission.React);
  const canVoiceMessage = has(permissions, Permission.SendVoiceMessages);
  const canPoll = has(permissions, Permission.SendPolls);
  // В личной переписке модераторов нет: закрепляет любой из двоих, пока может писать.
  const canPin = isDirect ? canSend : has(permissions, Permission.PinMessages);
  const store = useStore();
  // Правка своего последнего по ↑ в поле ввода и возврат фокуса после неё — как в Discord.
  const [editLast, setEditLast] = useState(0);
  const [composerFocus, setComposerFocus] = useState(0);
  const [following, setFollowing] = useState(false);

  // Лента уже взяла сообщение из ссылки при появлении (её эффект раньше этого):
  // дальше вести туда незачем, а при возврате в канал — и неверно.
  useEffect(() => {
    if (openAt) onOpened();
  }, [openAt, onOpened]);
  const canView = has(permissions, Permission.ViewChannel);

  const section = useRef<HTMLElement>(null);
  const [over, setOver] = useState<HTMLDivElement | null>(null);

  if (access && !canView) {
    return (
      <section className="sheet pane-surface">
        <div className="empties">
          <Empty
            title="Канал закрыт"
            body="Видеть его вам не разрешено. Обратитесь к тому, кто управляет правами сервера."
          />
        </div>
      </section>
    );
  }

  return (
    /*
      Лента и поле ввода — две панели, а не одна. Разделены тем же зазором,
      что и всё остальное: поле ввода не часть переписки, оно про то, что вы
      ещё только собираетесь сказать, и лежать внутри неё ему незачем.
    */
    <section className={docked ? "chat chat-docked" : "chat chat-feed"} ref={section}>
    <section className="main">
      {docked ? (
        <header className="panel-head docked-head">
          {!isDirect && <IconChat size={14} />}
          <span className="grow ellipsis">{title}</span>
          <button type="button" className="icon-btn" aria-label="Закрыть чат" title="Закрыть чат" onClick={docked}>
            <IconClose size={14} />
          </button>
        </header>
      ) : (
        <ChatHead
          title={title}
          topic={topic}
          isDirect={isDirect}
          side={side}
          hasMembers={hasMembers}
          threadUnread={store.threadUnread[channelId] ?? 0}
          onSide={onSide}
          onBack={onBack}
          onCall={onCall}
          callHint={callHint}
          callStatus={callStatus}
          threads={threads}
          pins={pins}
          search={search}
          isForum={isForum}
          isAnnouncement={isAnnouncement}
          onFollow={isAnnouncement ? () => setFollowing(true) : undefined}
        />
      )}
      {following && <FollowDialog channelId={channelId} channelName={title} onClose={() => setFollowing(false)} />}
      {callStrip}

      {/*
        Ключ по каналу — вместо сброса состояния эффектом.

        Лента и поле ввода держат при себе то, что верно только для одного
        канала: набранное, вложения, подсказку, бухгалтерию окна. При смене
        канала это надо забыть, и раньше забывали эффектом — то есть уже после
        отрисовки, вторым проходом. React умеет то же самое сам и в один
        проход: другой ключ — другой объект.
      */}
      {isForum ? (
        <Boundary area="форум" resetKeys={[channelId]}>
          <ForumView
            key={channelId}
            channelId={channelId}
            title={title}
            canSend={canSend}
            canManage={has(permissions, Permission.ManageChannel)}
            canModerate={canDeleteOthers}
            serverId={serverId}
            people={people}
            guidelines={topic}
          />
        </Boundary>
      ) : (
      <ToolbarHost.Provider value={over}>
      <Boundary area="лента" resetKeys={[channelId]}>
        <Feed
          key={channelId}
          channelId={channelId}
          canDeleteOthers={canDeleteOthers}
          canReact={canReact}
          canSend={canSend}
          canPin={canPin}
          announcement={isAnnouncement}
          threads={!isDirect && has(permissions, Permission.CreatePublicThreads)}
          people={people}
          custom={custom}
          channels={channels}
          roles={roles}
          myRoles={myRoles}
          onOpenProfile={onOpenProfile}
          onOpenChannel={onOpenChannel}
          editLast={editLast}
          onEditDone={() => setComposerFocus((count) => count + 1)}
          serverId={serverId}
          openAt={openAt}
          onOpenMessage={onOpenMessage}
          onOpenServer={onOpenServer}
          colorOf={colorOf}
          iconOf={iconOf}
          nickOf={nickOf}
          intro={isDirect ? undefined : { name: title, voice: !!docked, onEdit: onEditChannel }}
        />
      </Boundary>
      </ToolbarHost.Provider>
      )}
      {/* Слой над лентой: туда выносится ряд действий строки (см. `ToolbarHost`). */}
      <div className="feed-over" ref={setOver} />
      </section>

      {/*
        После блокировки закрывается только ввод — история остаётся читаемой
        обоим. Стирать её нечестно, и сервер её не стирает.
      */}
      {rulesPending && serverId && <RulesBar serverId={serverId} rules={rules} />}
      {!rulesPending && verifyUntil && <WaitBar until={verifyUntil} />}
      {!isForum && !rulesPending && !verifyUntil && (
      <Boundary area="поле ввода" resetKeys={[channelId]}>
        <Composer
          key={channelId}
          channelId={channelId}
          people={people}
          custom={custom}
          channels={channels}
          roles={roles}
          canUpload={canUpload}
          canVoiceMessage={canVoiceMessage}
          onEditLast={() => setEditLast((count) => count + 1)}
          onModerate={onModerate}
          onDirectMessage={onDirectMessage}
          focus={composerFocus}
          serverId={serverId}
          // Имя собеседника в дательном падеже не построить — «Написать Марат
          // Ильин» читается как ошибка, а оно и есть ошибка. Имя видно в шапке.
          placeholder={isDirect ? "Написать сообщение" : docked ? `Написать в «${title}»` : `Написать в #${title}`}
          disabled={!canSend}
          slowmode={slowmode}
          canEveryone={!isDirect && has(permissions, Permission.MentionEveryone)}
          stickers={stickers}
          canPoll={canPoll}
          disabledReason={
            blockedWith
              ? blockedWith.relationship === "blocked"
                ? `Вы заблокировали ${blockedWith.display_name || blockedWith.username}. Написать нельзя, история осталась.`
                : "Вас заблокировали. Написать нельзя, история осталась."
              : dmsClosed
                ? "Собеседник принимает личные сообщения только от друзей."
              : timeoutUntil
                ? `Вы в тайм-ауте до ${dayTime(timeoutUntil)}: читать можно, писать нельзя.`
                : "В этом канале вам нельзя писать."
          }
          onUnblock={
            blockedWith?.relationship === "blocked"
              ? () => void onUnblock(blockedWith.id)
              : undefined
          }
        />
      </Boundary>
      )}
    </section>
  );
}

/**
 * Шапка правой колонки: чему она принадлежит и крестик.
 *
 * Одна на все три её вида. Вид меняется, крестик остаётся на том же месте —
 * иначе он прыгал бы при переключении, и его приходилось бы искать заново
 * каждый раз.
 */
function SideHead({
  icon,
  title,
  onClose,
}: {
  icon: ReactNode;
  title: string;
  onClose: () => void;
}) {
  return (
    <header className="side-head">
      <span className="side-head-icon">{icon}</span>
      <span className="side-head-name">{title}</span>
      <span className="grow" />
      <Hint label="Закрыть">
        <button type="button" className="icon-btn" onClick={onClose}>
          <IconClose size={14} />
        </button>
      </Hint>
    </header>
  );
}

/** Закреплённые сообщения канала: колонка справа, список — `PinnedList`. */
function MemberColumn({
  serverId,
  channelId,
  members,
  roles,
  rights,
  meId,
  onOpenProfile,
  onWriteDirect,
  onInvite,
  onModerate,
  onClose,
}: {
  serverId: Uuid;
  /** Канал, где открыт список: в нём вызываются команды ботов на человека. */
  channelId: Uuid | null;
  members: Member[];
  roles: readonly Role[];
  rights: Rights | null;
  meId: Uuid | null;
  /** Профиль: с опорой — карточка рядом с ней, без — полное окно. */
  onOpenProfile: (id: Uuid, anchor?: HTMLElement) => void;
  onWriteDirect: (id: Uuid) => void;
  onInvite: (id: Uuid, serverId: Uuid) => void;
  onModerate: (what: { kind: "kick" | "ban" | "nickname"; id: Uuid; name: string; nickname?: string; self?: boolean }) => void;
  onClose: () => void;
}) {
  const store = useStore();
  const cache = useQueryClient();

  /*
   * Меню участника по правой кнопке — как в Discord. Действия без вопроса
   * (дружба, блок, роль) идут сразу; выгнать и бан — через окно с вопросом.
   */
  /** В тайм-ауте ли сейчас — по сроку, а не по флагу: срок истекает сам. */
  const timedOut = (member: Member) =>
    !!member.timeout_until && new Date(member.timeout_until).getTime() > Date.now();

  const commands = useChannelCommands(channelId);
  const itemsFor = (member: Member, name: string) => {
    const known = store.relationships.find((user) => user.id === member.id);
    const self = member.id === meId;
    return memberMenu({
      apps: channelId ? appItems(commands, 2, channelId, member.id) : [],
      self,
      name,
      handle: member.handle,
      relation: self ? "myself" : known?.relationship ?? "none",
      memberRoles: member.roles,
      roles,
      rights,
      timedOut: timedOut(member),
      actions: {
        profile: () => onOpenProfile(member.id),
        timeout: (minutes) =>
          void (minutes === null
            ? api.bans.untimeout(serverId, member.id)
            : api.bans.timeout(serverId, member.id, minutes)
          )
            .then(() => cache.invalidateQueries({ queryKey: keys.members(serverId) }))
            .catch(() => {}),
        write: () => onWriteDirect(member.id),
        invite: {
          servers: store.servers.filter((server) => server.id !== serverId),
          send: (target) => onInvite(member.id, target),
        },
        friend: (add) =>
          void (add ? api.relationships.add(member.id) : api.relationships.remove(member.id))
            .then(() => store.reloadRelationships())
            .catch(() => {}),
        block: (block) =>
          void (block ? api.relationships.block(member.id) : api.relationships.unblock(member.id))
            .then(() => Promise.all([store.reloadRelationships(), store.reloadDirects()]))
            .catch(() => {}),
        role: (roleId, on) =>
          void (on ? api.roles.assign(serverId, member.id, roleId) : api.roles.unassign(serverId, member.id, roleId))
            .then(() => cache.invalidateQueries({ queryKey: keys.members(serverId) }))
            .catch(() => {}),
        nickname: () =>
          onModerate({ kind: "nickname", id: member.id, name, nickname: member.nickname ?? undefined, self }),
        kick: () => onModerate({ kind: "kick", id: member.id, name }),
        ban: () => onModerate({ kind: "ban", id: member.id, name }),
      },
    });
  };

  /*
   * Группы — как в Discord: в сети — под старшей ролью с «показывать
   * отдельно», остальные — «в сети», кто не в сети — одной группой.
   *
   * Присутствие берётся из живого набора, а список лишь задаёт исходное
   * состояние: пока колонка открыта, люди приходят и уходят событиями.
   */
  const isOnline = (member: Member) => store.online.has(member.id) || member.online;
  const groups = memberGroups(members, roles, isOnline);

  return (
    <aside className="side-col glass-host">
      <SideHead
        icon={<IconMembers size={14} />}
        title="Участники"
        onClose={onClose}
      />

      <div className="side-col-body">
      {groups.map(({ title, members: items, dim }) => {
        return (
          <div key={title}>
            <div className="member-cap">
              {title} — {items.length}
            </div>

            {items.map((member) => {
              const known = store.relationships.find((user) => user.id === member.id);
              const person = {
                id: member.id,
                // Ник на сервере — поверх общего имени, как в Discord.
                name: member.nickname || member.display_name || member.username,
                handle: member.handle,
                avatarId: member.avatar_id,
                decorationId: member.decoration_id ?? null,
                // Отношения впереди состава: там профиль полнее. Но теперь
                // режим приходит и с составом — соседу по серверу он виден
                // так же, как другу.
                presence: known?.status_presence ?? member.status_presence,
                online: isOnline(member),
              };

              return (
                <MenuOn key={member.id} title={person.name} sub={member.handle} items={withId(itemsFor(member, person.name), member.id)}>
                <button
                  type="button"
                  className={`member ${dim ? "offline" : ""}`}
                  onClick={(event) => onOpenProfile(member.id, event.currentTarget)}
                >
                  <Tile person={person} size={30} />
                  <span className="grow" style={{ minWidth: 0 }}>
                    {/* Цвет имени — старшей цветной роли, как в Discord. */}
                    <span
                      className="member-name ellipsis"
                      style={{ display: "block", color: roleColorOf(member.roles, roles) ?? undefined }}
                    >
                      {person.name}
                      <TagChip tag={member.tag} />
                      {timedOut(member) && (
                        <span className="member-timeout" title={`Тайм-аут до ${dayTime(member.timeout_until!)}`}>
                          {" "}
                          <IconTimer size={12} />
                        </span>
                      )}
                    </span>
                    {(member.status_text || member.status_emoji) && (
                      <span className="member-status ellipsis" style={{ display: "block" }}>
                        {member.status_emoji && <span className="status-mark">{member.status_emoji}</span>}
                        {member.status_text}
                      </span>
                    )}
                  </span>
                </button>
                </MenuOn>
              );
            })}
          </div>
        );
      })}
      </div>
    </aside>
  );
}

/**
 * Сервер создаётся за один шаг — с нуля или по шаблону, как в Discord;
 * вступают только по коду из десяти знаков.
 *
 * Пока модерации нет, создают только администраторы Airel (`privileged`,
 * сервер проверяет то же самое): остальным окно предлагает одно приглашение.
 */
function CreateOrJoin({
  canCreate,
  onClose,
  onDone,
}: {
  canCreate: boolean;
  onClose: () => void;
  /** Куда попали: сервер и, если приглашение вело в канал, сам канал. */
  onDone: (id: Uuid, channel: Uuid | null) => void;
}) {
  const [name, setName] = useState("");
  const [template, setTemplate] = useState<ServerTemplate>(SERVER_TEMPLATES[0]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function run(action: () => Promise<{ server: Uuid; channel?: Uuid | null }>) {
    setBusy(true);
    setProblem(null);
    try {
      const done = await action();
      onDone(done.server, done.channel ?? null);
    } catch (error) {
      setProblem(
        error instanceof ApiError && error.status === 404
          ? "Такого кода нет — проверьте написание."
          : error instanceof ApiError && error.code === "invites_paused"
            ? "Приглашения на этот сервер приостановлены."
          : error instanceof ApiError && error.code === "server_creation_closed"
            ? "Создавать серверы пока могут только администраторы Airel."
          : error instanceof ApiError
            ? error.message
            : "Не получилось",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="Сервер" onClose={onClose}>
      {canCreate ? (
        <>
          <div className="field">
            <span className="field-label">Шаблон</span>
            <div className="server-templates" role="radiogroup" aria-label="Шаблон сервера">
              {SERVER_TEMPLATES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="radio"
                  aria-checked={template.id === item.id}
                  className={`server-template ${template.id === item.id ? "on" : ""}`}
                  onClick={() => setTemplate(item)}
                >
                  <span className="server-template-sign" aria-hidden>
                    {item.sign}
                  </span>
                  <span className="ellipsis">{item.title}</span>
                </button>
              ))}
            </div>
          </div>
          <Field label="Название нового сервера">
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Button
            type="button"
            className="w-full"
            disabled={busy || !name.trim()}
            onClick={() => void run(() => createFromTemplate(name.trim(), template))}
          >
            Создать
          </Button>

          <div className="rule" />
        </>
      ) : (
        <p className="small muted">
          Создавать серверы пока могут только администраторы Airel. Попасть на сервер можно по приглашению.
        </p>
      )}

      <Field label="Код приглашения" hint="Десять знаков, без похожих друг на друга символов">
        <Input
          className="mono"
          value={code}
          placeholder="dxrkcw4xky"
          // Вставить можно и ссылку — код из неё возьмём сами.
          onChange={(event) => setCode(inviteCodeOf(event.target.value))}
        />
      </Field>
      <Button
        variant="secondary"
        type="button"
        className="w-full"
        disabled={busy || !code}
        onClick={() =>
          void run(async () => {
            const used = await api.invites.use(code);
            if (used.joined) greet(used.server_id);
            // Приглашение в канал — открываем сразу его, как в Discord.
            return { server: used.server_id, channel: used.channel_id ?? null };
          })
        }
      >
        Войти по коду
      </Button>

      {problem && <div className="small danger-text">{problem}</div>}
    </Dialog>
  );
}

/** Шаг истории переходов: где были, какой канал и не страница ли друзей. */
type Step = { place: Place; channelId: Uuid | null; friends: boolean };

function sameStep(a: Step, b: Step): boolean {
  const samePlace = a.place.kind === b.place.kind && (a.place.kind !== "server" || (b.place.kind === "server" && a.place.id === b.place.id));
  return samePlace && a.channelId === b.channelId && a.friends === b.friends;
}
